import React, {
    useRef,
    useState,
    useMemo,
    useCallback,
    forwardRef,
    useImperativeHandle,
    useEffect
} from 'react';
import { gridRect, tradeCardKeys, cardKeysFor } from '../utils/tradeDecorator';
import { BAR_HIT_PADDING_PX } from '../utils/chartHitZone';
import { formatAxisPrice } from '../utils/priceFormat';
import { indicatorRows, macdDecimals } from '../utils/indicatorTooltip';
import { resolveColor } from '../utils/workspaceColors';
import { RSI_LINE_LIGHT, RSI_LINE_DARK } from '../utils/indicatorPalette';
import { symbolReportsVolume } from '../constants/assetClasses';
import { layoutCards, cardGraphics } from '../utils/tradeCardLayout';
import { yearsOnChart } from '../utils/yearlySummary';
// The SHARED inward snap, used by the replay's year buttons and by focusDateRange below. One
// definition, so a date range focused by a command lands on the same bars as one focused by a
// click -- two snaps would differ on exactly the weekend bounds they both exist to handle.
import { focusWindow } from '../utils/replayViewport';
import PropTypes from 'prop-types';
import ReactECharts from 'echarts-for-react';
import { forceFutureMarkerStyle } from '../utils/forceFutureMarkerStyle';
import { pricePatchSeries } from '../utils/pricePatch';
import { Box } from '@mui/material';

// The following imports are not used in the final logic but kept for context.
// import HorizontalLineDialog from './HorizontalLineDialog'; 
import { useChartOverlays } from '../context/ChartOverlayContext'; 
// import Draggable from 'react-draggable'; 

import { processChartData } from '../utils/chartDataProcessor';
import { DEFAULT_WATERMARK, WATERMARK_FILL, watermarkFillFor, watermarkForMarket } from '../utils/branding';
import { BASE_TEXT_SIZE } from '../utils/textAnnotationSizes';
import { useAuth } from '../context/AuthContext';
// The default view window's future span. Shared with the drawing tools, which size their
// projections against it — see FUTURE_PROJECTION_BARS and FIB_EXTENSION_BARS there.
import { FUTURE_VIEW_BARS } from '../utils/technicalDrawings';
import { SPECIAL_STUDY_MODE } from './BarFloaterSelectionDialog';
import { buildAspectFloaterLines, filterSummary, ORB_LIMIT_WITH_MOON, ORB_LIMIT_DEFAULT }
    from '../utils/aspectFloaterLayout';

// --- THEME CONSTANTS ---
// Moved outside the component to prevent re-creation on every render

/**
 * THE RSI LINE'S WIDTH — one point up from the 1 it shipped at.
 *
 * <p>Named rather than inlined because "a point thicker" is a relative instruction, and the only
 * way a later reader can tell 2 from an arbitrary number is to be told what it was.
 */
export const RSI_LINE_WIDTH = 2;

const lightThemeColors = {
    // INSTITUTIONAL, not retail. Muted dark slate for an up day and muted crimson for a down
    // one: at a full-history zoom a neon green/red chart reads as a heat map, and the studies
    // drawn over it have to shout to be seen at all.
    candlestickUp: '#2F4F4F',
    candlestickDown: '#8B0000',
    // The volume bars carry the SAME up/down semantic and are tinted from the same pair.
    // Leaving them neon beside slate candles would read as a rendering fault rather than a
    // palette -- they are the one thing on the chart that must agree with the candles.
    volumeUp: 'rgba(47, 79, 79, 0.40)',
    volumeDown: 'rgba(139, 0, 0, 0.40)',
    tooltipBackground: 'rgba(255, 255, 255, 0.9)',
    tooltipBorder: '#ccc',
    tooltipColor: '#333',
    backgroundColor: 'transparent',
    graphic: [{
        type: 'text',
        left: 'center',
        top: '35%',
        style: {
            // Not the live watermark: nothing reads theme.graphic — the chart builds its own
            // `graphic` from watermarkText/watermarkOverride. Kept as the theme's shape only.
            text: DEFAULT_WATERMARK,
            font: 'bold 250px sans-serif',
            fill: WATERMARK_FILL,
            textAlign: 'center',
            textVerticalAlign: 'middle',
        }
    }],
    axisLabelColor: '#6e7079',
    crosshairColor: '#999',
    // The RSI line's own colour, so it is a PALETTE entry rather than a literal at the series.
    // A reader's configured `rsiLine` overrides it; this is what an unconfigured install gets.
    //
    // From `indicatorPalette` rather than typed here, because the configuration dialog's swatch
    // needs the SAME value to open on -- and when it did not have it, the swatch showed grey for
    // a line that was drawing blue.
    rsiLine: RSI_LINE_LIGHT,
    primary: '#c23531',
    trendlineUp: '#000000',
    trendlineDown: '#ef4444',
    // THE PRICE GRID'S SPLIT LINES. This key existed only on the dark palette, so on light the
    // option read `undefined` and ECharts fell back to its own default -- which happened to look
    // reasonable and meant the light chart's grid was the one thing on it nobody had chosen.
    gridColor: '#e2e6ec',
    // The y-axis banding behind the price. Barely-there on both themes; declared because the
    // light values are invisible on charcoal and were hardcoded at the axis.
    splitAreaColors: ['rgba(250, 250, 250, 0.05)', 'rgba(200, 200, 200, 0.05)'],
    // The RSI pane's 0..100 rails and the volume pane's. Hardcoded '#ccc' at both axes, which on
    // charcoal is a brighter line than the data drawn over it.
    paneLineColor: '#cccccc',
};

/**
 * THE DARK CANVAS.
 *
 * <h2>⚠ The candles could not be shared, and that is the whole of this palette</h2>
 *
 * Both palettes used to carry the SAME two hexes — `#2F4F4F` slate up and `#8B0000` crimson down,
 * chosen to be institutional rather than retail against a white page. Against `#0d1117` they are
 * nearly invisible: measured, slate is **1.5:1** and crimson **1.4:1** on this background, where
 * 3:1 is the floor for a graphical object you are meant to be able to see at all.
 *
 * <p>The dark palette was never reachable — `App` hardcoded the light theme — so this had never
 * been looked at. Switching the theme without it would have produced a charcoal rectangle with
 * almost nothing on it, which is the "visual artifact" a theme change is most likely to cause and
 * the least likely to be called a bug rather than a blank chart.
 *
 * <p>The replacements keep the MUTED, non-neon intent exactly: a desaturated sea-green and a
 * desaturated rose, at **5.4:1** and **5.0:1**. At a full-history zoom they still read as a
 * price series rather than as a heat map, which is what the original note was protecting.
 *
 * <h2>Both palettes carry the same keys</h2>
 *
 * `gridColor` was on this one and not on the light one, and `backgroundColor` the other way
 * round, so each theme had a key the other resolved to `undefined`. A missing key does not fail —
 * ECharts substitutes its own default — so the symptom is a chart that is subtly not the one
 * anybody configured. `chartTheme.test.js` asserts the two key sets are identical.
 */
const darkThemeColors = {
    candlestickUp: '#4f9c8b',
    candlestickDown: '#c2616b',
    // The volume bars carry the SAME up/down semantic and are tinted from the same pair. Leaving
    // them on the light theme's slate beside these candles would read as a rendering fault
    // rather than a palette -- they are the one thing on the chart that must agree with the
    // candles.
    volumeUp: 'rgba(79, 156, 139, 0.40)',
    volumeDown: 'rgba(194, 97, 107, 0.40)',
    tooltipBackground: 'rgba(31, 38, 48, 0.95)',
    tooltipBorder: '#3a4452',
    tooltipColor: '#e6edf3',
    backgroundColor: 'transparent',
    gridColor: '#232a35',
    axisLabelColor: '#9aa7b4',
    crosshairColor: '#7d8896',
    // Lighter on dark: the light theme's blue against a dark panel is close to unreadable, and
    // the RSI line is thin. The hue is the same, which keeps the two themes one chart.
    rsiLine: RSI_LINE_DARK,
    // ⚠ THESE THREE ARE UNREAD, on both palettes, and were before this change.
    //
    // Nothing consumes `primary`, `trendlineUp` or `trendlineDown`: the trendline TOOL draws in
    // `overlay.color`, which is the reader's own, and `primary` is a leftover from an ECharts
    // theme shape. `chartTheme.test.js` names them as the exemption to "every key is read", so
    // they are a recorded fact rather than three values somebody may wire up believing they were
    // chosen. Found by that test, which is what it is for.
    //
    // Kept rather than deleted, and given dark-appropriate values: `#000000` here would be
    // 1.1:1 on this canvas, so the first caller to read `trendlineUp` would get an invisible
    // line and no reason to suspect the palette.
    primary: '#3fb950',
    trendlineUp: '#e6edf3',
    trendlineDown: '#f85149',
    // The light theme's near-white banding would be a pair of bright stripes here.
    splitAreaColors: ['rgba(255, 255, 255, 0.022)', 'rgba(255, 255, 255, 0.045)'],
    paneLineColor: '#2b3340',
};

// --- FIX: Moved computeRSI function out of useMemo to prevent scoping/syntax errors ---
export const computeRSI = (bars, period = 14) => {
    if (!bars || bars.length < period + 1) return [];

    const rsiArr = [];
    let gainSum = 0, lossSum = 0;

    // Calculate initial average gain/loss for the first 'period' bars
    for (let i = 1; i <= period; i++) {
        const diff = bars[i][1] - bars[i - 1][1];
        gainSum += diff > 0 ? diff : 0;
        lossSum += diff < 0 ? -diff : 0;
    }

    let avgGain = gainSum / period;
    let avgLoss = lossSum / period;

    // Fill initial nulls for the period where RSI is not yet calculated
    for (let i = 0; i < period; i++) {
        rsiArr.push(null);
    }

    // Calculate first RSI value and place it at the correct index
    let rs = avgLoss === 0 ? Number.POSITIVE_INFINITY : avgGain / avgLoss;
    rsiArr[period] = avgLoss === 0 ? 100 : 100 - (100 / (1 + rs));

    // Calculate subsequent RSI values using Wilder's smoothing
    for (let i = period + 1; i < bars.length; i++) {
        const diff = bars[i][1] - bars[i - 1][1];
        const gain = diff > 0 ? diff : 0;
        const loss = diff < 0 ? Math.abs(diff) : 0;
        const avgGainSm = (avgGain * (period - 1) + gain) / period;
        const avgLossSm = (avgLoss * (period - 1) + loss) / period;
        rs = avgLossSm === 0 ? Number.POSITIVE_INFINITY : avgGainSm / avgLossSm;
        rsiArr.push(avgLossSm === 0 ? 100 : 100 - (100 / (1 + rs)));
        avgGain = avgGainSm;
        avgLoss = avgLossSm;
    }
    return rsiArr;
}
// ------------------------------------------------------------------------------------


const findClosestTradingDayIndex = (targetDate, allDates) => {
    let searchRadius = 0;
    while (searchRadius < 10) { // Limit search to +/- 10 days
        let forwardDate = new Date(targetDate);
        forwardDate.setDate(forwardDate.getDate() + searchRadius);
        let forwardDateString = forwardDate.toISOString().split('T')[0];
        let index = allDates.indexOf(forwardDateString);
        if (index !== -1) return index;

        if (searchRadius > 0) {
            let backwardDate = new Date(targetDate);
            backwardDate.setDate(backwardDate.getDate() - searchRadius);
            let backwardDateString = backwardDate.toISOString().split('T')[0];
            index = allDates.indexOf(backwardDateString);
            if (index !== -1) return index;
        }
        searchRadius++;
    }
    return -1; // Not found within reasonable range
};

/**
 * THE POINTER LABEL FOLLOWS THE AXIS, which is the whole of the crosshair fix.
 *
 * <h2>A hidden axis still labels the crosshair, and that is what was covering the price</h2>
 *
 * ECharts draws an axis-pointer label for every axis on the hovered grid whether or not the
 * axis is shown — and a hidden axis has no offset, so its label lands exactly where the shown
 * one's does. Measured on a live chart with the cursor at y=334 of the price grid, THREE labels
 * rendered on the horizontal line:
 *
 * <pre>
 *   541.84   x=3276   yAxis 0,  price, position right     <- the one that was wanted
 *   0.677    x=3283   yAxis 5,  Declination, show:FALSE   <- drawn ON TOP of it
 *   67.73    x=0      yAxis 11, Distance %, show:FALSE    <- the left-hand reading
 * </pre>
 *
 * So the right-hand label was the hidden Declination axis's value for that pixel, seven pixels
 * from the price it was hiding. Nothing was broken about the price axis; it was being covered.
 *
 * <p>Tying the pointer label to the axis's own `show` flag fixes it without taking anything
 * away: switch Declination on and its label returns, at its own 60px offset where it collides
 * with nothing.
 *
 * @param show the axis's own visibility — the same expression passed to `show`
 */
const pointerLabel = (show) => ({ axisPointer: { label: { show: !!show } } });

const EChartComponent = forwardRef((props, ref) => {
    // Destructure props.
    const { 
        showRSI = false, 
        rsiData: propRsiData = [],
        maConfigs,
        barData: propsBarData,
        theme,
        loading: propsLoading,
        chartType,
        viewMode,
        chartColors: propsChartColors,
        overlays,
        showMACD,
        macdData,
        setPatternDialogOpen,
        onBarSelect,
        barFloaterMode = 'ohlcv',
        // The toolbar preset, not the floater selection. See utils/toolbarBarMode.
        shortBarMode = false,
        /**
         * ⚠ AN ADMINISTRATOR IS AN EXCEPTION TO THE RULES MADE FOR OTHER READERS.
         *
         * Stated as a principle rather than discovered per feature, because the alternative was
         * being discovered per feature: three separate cards -- celestial, Deep Pivot, and the CTS
         * aspect breakdown -- each shipped invisible and each needed its own narrow exemption, for
         * the same reason every time. `barFloaterMode` defaults to 'ohlcv', so every rule keyed on
         * `suppressAstroTooltips` is ON for everybody out of the box.
         *
         * What it exempts is deliberately limited to HOVER CARDS. It does not touch the product
         * deny-lists, the role gates on the toolbar, or anything a customer could see; it decides
         * whether a card an administrator has deliberately summoned is allowed to appear.
         */
        isAdmin = false,
        /**
         * The customer's configured chart colours, by role. Empty means every element keeps the
         * colour its own study owns, which is what an unconfigured install renders.
         */
        workspaceColors = {},
        /**
         * The last bar with price on it, as a date, or null when nothing is masked.
         *
         * <p>Read by the rail-anchored marker branch, which needs to know PER BAR whether there
         * is a price to draw a mark at. It cannot infer that from `barData`: the Workbench's
         * replay masks the price by merging nulls onto the series by id, after this has run, so
         * the data this sees is unmasked in exactly the mode that needs the answer most.
         */
        revealedUpToDate = null,
        lastTradedDate = null,
        symbol,
        watermarkOverride,
        watermarkText,
        onUpdateOverlay,
        // The replay reveal index, or null when not replaying. See the final gate at the end of
        // finalChartOptions - the padded case needs nothing, being read from the bars themselves.
        simulationBoundary = null,
    } = props;

    const { api, user } = useAuth();

    /**
     * Brand for the watermark.
     *
     * Derived here from the signed-in user rather than relying only on the watermarkText prop.
     * The prop is still honoured — App passes it — but a watermark that silently falls back to the
     * wrong brand whenever one link in a prop chain is missed is not worth the coupling, and this
     * component already has the auth context to hand.
     */
    const resolvedWatermark =
        watermarkText || watermarkForMarket(user?.marketId ?? user?.market);

    // ── Bar Floater state ──────────────────────────────────────────────
    const [floaterData, setFloaterData] = useState(null);
    const [floaterPos, setFloaterPos] = useState({ x: 0, y: 0, visible: false });
    const floaterCacheRef = useRef({});  // { 'yyyy-MM-dd': { planets: [...], aspects: [...] } }
    const floaterModeRef = useRef(barFloaterMode);
    useEffect(() => { floaterModeRef.current = barFloaterMode; }, [barFloaterMode]);

    // Read through a ref by the tooltip formatter, which is built into the chart option and
    // therefore captures whatever value was in scope when the option was last rebuilt. A stale
    // capture here would leave the lock off for the rest of the session after a mode switch.
    const shortBarModeRef = useRef(shortBarMode);
    useEffect(() => { shortBarModeRef.current = shortBarMode; }, [shortBarMode]);

    /**
     * The Special Study is PINNED BY A CLICK, and every other mode follows the pointer.
     *
     * <p>The two cannot share a trigger. Every other floater is a lookup that a moving pointer can
     * afford to repeat; this one walks the symbol's pivots and computes a sky for each, so firing it
     * on mousemove would queue a request per pixel and make the card flicker between bars while it
     * is being read. Explicitly click-driven, and it stays until another bar, the close button, or a
     * click on the background.
     */
    const isSpecialFloater = barFloaterMode === SPECIAL_STUDY_MODE;
    const isSpecialRef = useRef(isSpecialFloater);
    useEffect(() => { isSpecialRef.current = isSpecialFloater; }, [isSpecialFloater]);
    const symbolRef = useRef(symbol);
    useEffect(() => { symbolRef.current = symbol; }, [symbol]);
    // Cleared when the mode changes, so leaving Special Study does not leave its card behind.
    useEffect(() => {
        setFloaterPos(p => ({ ...p, visible: false }));
        setFloaterData(null);
        floaterDateRef.current = null;
    }, [barFloaterMode]);
    const floaterDateRef = useRef(null);
    const floaterFetchingRef = useRef(null);
    const fetchFloaterRef = useRef(null);

    // Clear cache when mode changes since different modes return different data
    useEffect(() => { floaterCacheRef.current = {}; }, [barFloaterMode]);

    const fetchFloaterData = useCallback(async (dateStr) => {
        const curMode = floaterModeRef.current;
        if (!dateStr || curMode === 'ohlcv') return;
        // Keyed on the symbol too: the Special Study reads the symbol's own pivots, so the same
        // date is a different answer on a different chart. The other modes are symbol-independent
        // and the extra key segment costs them nothing.
        const cacheKey = `${curMode}:${symbolRef.current || '-'}:${dateStr}`;
        if (floaterCacheRef.current[cacheKey]) {
            setFloaterData(floaterCacheRef.current[cacheKey]);
            return;
        }
        if (floaterFetchingRef.current === cacheKey) return;
        floaterFetchingRef.current = cacheKey;
        try {
            if (curMode === SPECIAL_STUDY_MODE) {
                const resp = await api.post('/api/special-study',
                    { date: dateStr, symbol: symbolRef.current || null });
                floaterCacheRef.current[cacheKey] = resp.data;
                if (floaterDateRef.current === dateStr) setFloaterData(resp.data);
                return;
            }
            const isAspects = curMode.startsWith('aspects');
            const mode = isAspects ? 'aspects' : 'planets';
            const coordSystem = curMode === 'aspects_helio' ? 'helio'
                              : curMode === 'planets_trop'  ? 'tropical'
                              : curMode === 'planets_sid'   ? 'sidereal'
                              : 'geo';
            const resp = await api.get(`/api/bar-floater-data?date=${dateStr}&mode=${mode}&coordSystem=${coordSystem}`);
            floaterCacheRef.current[cacheKey] = resp.data;
            if (floaterDateRef.current === dateStr) setFloaterData(resp.data);
        } catch (e) {
            console.warn('[Floater] fetch error', e);
        } finally {
            if (floaterFetchingRef.current === cacheKey) floaterFetchingRef.current = null;
        }
    }, [api]);
    useEffect(() => { fetchFloaterRef.current = fetchFloaterData; }, [fetchFloaterData]);

    useChartOverlays();

    // --- Zoom Persistence ---
    const zoomRef = useRef({ startValue: null, endValue: null });
    const astroSeriesRef = useRef(null);  // full AstroGraph numeric array for dynamic rescaling on zoom
    // Adding a drawing overlay should NOT reset the zoom.
    const prevOverlayCountRef = useRef((overlays || []).length);
    useEffect(() => {
        const count = (overlays || []).length;
        if (count === 0 && prevOverlayCountRef.current > 0) {
            zoomRef.current = { startValue: null, endValue: null };
        }
        prevOverlayCountRef.current = count;
    }, [overlays]);

    // Track the last three clicked bar indices
    const [showCycleMarkers, setShowCycleMarkers] = useState(false);
    const [showPatterns] = useState(false); 
    
    // --- REFS AND LOCAL STATE ---
    const chartRef = useRef(null);

    const [dividerPercent] = useState(80);

    /**
     * WHICH TRADE DECORATORS ARE OPEN, as a Set of `${tradeKey}:${half}`.
     *
     * <p>Empty by default, deliberately: the brief is that these must NOT clutter the chart, and a
     * run can draw hundreds of trades. The set is the whole of the feature's state -- a click
     * adds or removes one key, and the option is rebuilt from it.
     *
     * <p>Keyed by the trade's own identity rather than by array position, so a re-run that returns
     * one trade more does not move every open card onto a different trade.
     */
    const [openDecorators, setOpenDecorators] = useState(() => new Set());

    /**
     * Whether the YEARLY SUMMARY plaques are drawn. Off by default — see `setYearlySummaries`.
     */
    const [showYearlySummaries, setShowYearlySummaries] = useState(false);

    /* ── the trade cards, drawn as draggable `graphic` elements ─────────────────────────────── */

    /**
     * The card DESCRIPTORS from the last option build.
     *
     * <p>A ref rather than state, and assigned inside the option memo. The memo already computes
     * them -- they fall out of the same pass over the overlays -- and returning them alongside the
     * option would mean changing what every consumer of the memo receives. The assignment is
     * idempotent for a given render, so it is safe under a double-invoked render.
     */
    const tradeCardsRef = useRef([]);

    /**
     * WHERE THE READER HAS PUT EACH CARD, as a delta from that card's own default.
     *
     * <p>A ref, deliberately not state: a drop must NOT re-render the tree. The card is already
     * sitting where it was dropped -- zrender moved it -- so re-rendering would at best repaint
     * it in the same place and at worst fight the gesture. The offset is read on the next rebuild
     * (a zoom, a new run, a card opened) and applied there.
     *
     * <p>A DELTA rather than an absolute position, so a dragged card keeps its relationship to
     * its own bar through a zoom instead of being stranded where the pixels used to be.
     */
    const cardOffsetsRef = useRef(new Map());

    /** Coalesces repositioning to one pass per frame, since a zoom fires continuously. */
    const cardFrameRef = useRef(0);

    /**
     * The scheduler, reachable from the STABLE `onEvents` object.
     *
     * <p>`onEvents` must not be rebuilt between renders -- echarts-for-react disposes and
     * recreates the whole instance when it is, which flickers the chart. The scheduler closes
     * over the current option and therefore changes every render, so the handler reads it
     * through here instead of capturing it.
     */
    const scheduleCardsRef = useRef(null);

    /**
     * The chart's pixel size, for the decorator clamp.
     *
     * <p>Tracked as state rather than read inline because the option is built during render, when
     * the instance's size for THIS render is what matters -- and because a resize has to rebuild
     * the option or the clamp would go on holding cards inside the previous rectangle.
     */
    const [chartPixelSize, setChartPixelSize] = useState(null);

    /**
     * Keeps the decorator clamp's rectangle in step with the chart's actual size.
     *
     * <p>Only sets state when the size has really changed. Writing it unconditionally would set
     * state on every render of a component that re-renders on every zoom, which is a loop.
     */
    useEffect(() => {
        const read = () => {
            const instance = chartRef.current?.getEchartsInstance?.();
            // Guarded the same way every other instance call in this file is. The instance is not
            // fully formed on the first pass, and a partial one -- a test double, or an older
            // echarts build -- may not carry the size getters at all. An unguarded call here threw
            // during mount and took the whole chart down with it, which is a steep price for a
            // label clamp.
            if (typeof instance?.getWidth !== 'function'
                || typeof instance?.getHeight !== 'function') return;
            const width = instance.getWidth();
            const height = instance.getHeight();
            if (!Number.isFinite(width) || !Number.isFinite(height)) return;
            setChartPixelSize((prev) =>
                (prev && prev.width === width && prev.height === height
                    ? prev
                    : { width, height }));
        };
        read();
        // Once more after the first paint: on mount the instance exists but has not been laid out,
        // so its size is 0 and the clamp would hold every card inside a degenerate rectangle.
        const settle = setTimeout(read, 0);
        window.addEventListener('resize', read);
        return () => {
            clearTimeout(settle);
            window.removeEventListener('resize', read);
        };
        // DEPENDENCIES, not a bare effect. Written without an array first, which re-ran it and
        // re-registered the listener on EVERY render of a component that re-renders on every zoom
        // -- visible churn, and the kind that gets worse the more overlays are on the chart.
        // Mount and a data change are when the chart is (re)built, and those plus a window resize
        // are the only ways its pixel size moves.
    }, [propsBarData]);


    /**
     * Set when a click lands on a trade line, read and cleared by the DOM-level handler.
     *
     * <p>ECharts dispatches a series click from zrender on the canvas; the wrapper's own listener
     * then fires in the bubble phase. Without this flag one gesture on a trade line would ALSO
     * start a drawing or run a spiral inspection -- two results from one click, which is the
     * collision the spiral branch's ordering already guards against for the other tools.
     */
    const tradeClickRef = useRef(false);

    // Expose methods to parent via ref
    /**
     * EVERY TRADE CARD ON THE CHART, as decorator keys.
     *
     * <p>Derived from the overlays rather than tracked alongside them, so "all" always means what
     * is actually drawn right now. A separately maintained list would go stale the moment a run
     * is re-run with one trade more, and the symptom would be cards that cannot be opened and
     * keys that open nothing.
     */
    const allTradeCardKeys = useCallback(() => tradeCardKeys(overlays), [overlays]);

    /**
     * Tells the host how many cards are open, so the Admin Panel's button can label itself.
     *
     * <p>Pushed rather than pulled: the count is state in here, and a host reading it through the
     * ref during render would read a stale value and never re-render when a card is toggled by
     * clicking the chart.
     */
    const onTradeCardsChanged = props.onTradeCardsChanged;
    useEffect(() => {
        if (typeof onTradeCardsChanged === 'function') onTradeCardsChanged(openDecorators.size);
    }, [openDecorators, onTradeCardsChanged]);

    useImperativeHandle(ref, () => ({
        setShowCycleMarkers,
        toggleCycleMarkers: () => setShowCycleMarkers((prev) => !prev),
        getShowCycleMarkers: () => showCycleMarkers,
        setPatternDialogOpen: setPatternDialogOpen,
        getEchartsInstance: () => chartRef.current.getEchartsInstance(),

        /** How many trade cards exist to be shown — so the panel can label its own button. */
        countTradeCards: () => allTradeCardKeys().length,
        /** How many are open right now. */
        countOpenTradeCards: () => openDecorators.size,

        /**
         * Shows or hides the YEARLY SUMMARY plaques, and reports how many there now are.
         *
         * <p>Off by default. They are a chart-book device: one gold plaque per visible year
         * across the top is exactly right for a screenshot and in the way during ordinary
         * analysis, so this is a deliberate act like opening the cards rather than a default.
         */
        setYearlySummaries: (visible) => {
            setShowYearlySummaries(!!visible);
            return visible ? yearsOnChart(overlays) : 0;
        },
        countYearlySummaries: () => yearsOnChart(overlays),
        yearlySummariesShown: () => showYearlySummaries,

        /**
         * Opens or closes EVERY trade card at once.
         *
         * <p>A whole new Set either way, never a mutation of the existing one: React compares the
         * state by identity, and mutating in place would update nothing while looking correct in
         * the debugger. The option memo depends on this value, so a new Set is also what makes
         * the chart rebuild.
         */
        setAllTradeCards: (visible) => {
            const next = visible ? new Set(allTradeCardKeys()) : new Set();
            setOpenDecorators(next);
            return next.size;
        },

        /**
         * ZOOMS TO THE FIRST FULL CALENDAR YEAR of the loaded series.
         *
         * <p>`startValue`/`endValue` with CATEGORY values, not `start`/`end` percentages. The x
         * axis is a category axis of date strings, so a percentage is a position in the bar list
         * and the arithmetic for "which percent is 31 December" depends on how many sessions that
         * year happened to have. Naming the dates lets ECharts do it.
         *
         * <p>Targeted by `xAxisIndex`, never by dispatching bare. A `dataZoom` action with no
         * target hits EVERY zoom component including the two bound to the y axis, which collapses
         * the price scale to a sliver -- measured, while debugging the trade decorator.
         */
        zoomToFirstCalendarYear: () => {
            const dates = propsBarData && Array.isArray(propsBarData.dates)
                ? propsBarData.dates : [];
            if (dates.length === 0) return null;
            const year = String(dates[0]).slice(0, 4);
            // The LAST bar of that year, not 31 December: the chart is indexed by trading
            // sessions, and naming a date the series does not contain leaves ECharts to guess.
            let last = 0;
            while (last + 1 < dates.length && String(dates[last + 1]).slice(0, 4) === year) {
                last += 1;
            }
            const instance = chartRef.current?.getEchartsInstance?.();
            if (!instance) return null;
            instance.dispatchAction({
                type: 'dataZoom', xAxisIndex: 0,
                startValue: dates[0], endValue: dates[last],
            });
            return { year, from: dates[0], to: dates[last], bars: last + 1 };
        },

        /**
         * MOVES THE VIEWPORT TO A DATE RANGE. The Command Bus's `chart.focusDateRange`.
         *
         * <h2>⚠ IT DISPATCHES; IT NEVER SETS STATE</h2>
         *
         * The zoom window is deliberately not React state — see the `datazoom` handler below,
         * which records it in `zoomRef` and says in as many words why calling `setZoomWindow`
         * there "would trigger memo recomputation -> notMerge:true full rebuild on every scroll,
         * which corrupts the chart". A command that moved the view through state would do that
         * to every caller, and it would drop the overlays the caller had just drawn.
         *
         * <h2>Resolved to INDICES through `focusWindow`, not to date strings</h2>
         *
         * `zoomRef` holds indices — the fallback a few hundred lines above is `initialStart = 0`
         * — and the datazoom event reports indices back for a category axis. Dispatching a date
         * string would work and would then be recorded as an index, so the two would agree only
         * by ECharts' good manners. `focusWindow` is also where the SNAP already lives, shared
         * with the replay's year buttons: the start resolves to the first bar at or AFTER it and
         * the end to the last bar at or BEFORE it, because neither bound is reliably a trading
         * day and a date the series does not contain leaves ECharts to guess.
         *
         * <h2>⚠ TARGETED BY `xAxisIndex`, and the brief's snippet omits it</h2>
         *
         * A bare `dataZoom` action hits EVERY zoom component including the two bound to the y
         * axis, which collapses the price scale to a sliver — measured, and recorded on
         * `zoomToFirstCalendarYear` directly above. The same correction applies here.
         *
         * @param {?string} fromDate `yyyy-mm-dd`, or null/undefined for "from the beginning"
         * @param {?string} toDate   `yyyy-mm-dd`, or null/undefined for "to the end"
         * @returns {?object} `{from, to, startValue, endValue, bars}`, or null when refused
         */
        focusDateRange: (fromDate, toDate) => {
            const dates = propsBarData && Array.isArray(propsBarData.dates)
                ? propsBarData.dates : [];
            if (dates.length === 0) return null;

            // Validated rather than trusted: the axis is ISO strings compared as strings, so a
            // malformed bound like "2024" or "01/03/2024" does not fail — it compares
            // lexicographically and lands on a plausible, wrong bar.
            const ISO = /^\d{4}-\d{2}-\d{2}$/;
            const from = fromDate == null || fromDate === '' ? null : String(fromDate);
            const to = toDate == null || toDate === '' ? null : String(toDate);
            if ((from !== null && !ISO.test(from)) || (to !== null && !ISO.test(to))) return null;

            const view = focusWindow(dates, { from, to });
            if (!view) return null;

            const instance = chartRef.current?.getEchartsInstance?.();
            if (!instance) return null;

            // Mirrored BEFORE the dispatch so the window survives the next option rebuild even
            // if the datazoom event does not fire. When it does, it overwrites this with
            // ECharts' own post-clamp values, which are the more accurate pair -- so recording
            // it here can only ever be a floor, never a disagreement.
            zoomRef.current = { startValue: view.startValue, endValue: view.endValue };
            instance.dispatchAction({
                type: 'dataZoom', xAxisIndex: 0,
                startValue: view.startValue, endValue: view.endValue,
            });
            return {
                from: dates[view.startValue],
                to: dates[view.endValue],
                startValue: view.startValue,
                endValue: view.endValue,
                bars: view.endValue - view.startValue + 1,
            };
        },
    }), [showCycleMarkers, setPatternDialogOpen, allTradeCardKeys, openDecorators, propsBarData,
        overlays, showYearlySummaries]);

    // --- Stable click handler via native DOM (survives ECharts instance recreation) ---
    const chartWrapperRef = useRef(null);
    const onBarSelectRef = useRef(onBarSelect);
    useEffect(() => { onBarSelectRef.current = onBarSelect; }, [onBarSelect]);
    const onBarContextMenuRef = useRef(props.onBarContextMenu);
    useEffect(() => { onBarContextMenuRef.current = props.onBarContextMenu; }, [props.onBarContextMenu]);
    /*
     * THROUGH A REF, because `stableOnEvents` must not be rebuilt between renders -- that memo's
     * own comment says why: echarts-for-react disposes and recreates the whole instance when the
     * object identity changes. The host's handler closes over its session state and so is a new
     * function every click; naming it as a dependency would tear the chart down and rebuild it
     * on every click.
     */
    const onSignalClickRef = useRef(props.onSignalClick);
    useEffect(() => { onSignalClickRef.current = props.onSignalClick; }, [props.onSignalClick]);
    const barDatesRef = useRef(props.barData?.dates);
    useEffect(() => { barDatesRef.current = props.barData?.dates; }, [props.barData?.dates]);
    const mouseDownPos = useRef(null);

    /**
     * The bar under a mouse event, or null when the pointer is outside the price grid.
     *
     * Shared by the click and context-menu handlers so both resolve a bar the same way. Two copies
     * of this arithmetic would be two chances for the menu to open on a different bar than the one
     * a click would have selected.
     */
    const barAtEvent = useCallback((e) => {
        const wrapper = chartWrapperRef.current;
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!wrapper || !instance) return null;
        const rect = wrapper.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        if (!instance.containPixel({ gridIndex: 0 }, [offsetX, offsetY])) return null;
        const pointInGrid = instance.convertFromPixel({ gridIndex: 0 }, [offsetX, offsetY]);
        if (!pointInGrid) return null;
        const dataIndex = Math.round(pointInGrid[0]);
        const dates = barDatesRef.current;
        if (!dates || dataIndex < 0 || dataIndex >= dates.length) return null;
        return { date: dates[dataIndex], dataIndex, price: pointInGrid[1] };
    }, []);

    useEffect(() => {
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;
        const onDown = (e) => { mouseDownPos.current = { x: e.clientX, y: e.clientY }; };
        const onClick = (e) => {
            // A TRADE LINE ALREADY CONSUMED THIS GESTURE.
            //
            // ECharts dispatches its series click from zrender on the canvas; this listener then
            // fires in the bubble phase of the same gesture. Without this the one click would
            // toggle a decorator AND start a drawing or run a spiral inspection -- two results
            // from one gesture, which is precisely the collision the spiral branch's ordering
            // guards against for the other tools. Cleared here so the flag can never outlive the
            // click that set it.
            if (tradeClickRef.current) {
                tradeClickRef.current = false;
                return;
            }
            // Ignore drags (scroll / zoom gestures) — only true clicks (< 5px movement)
            if (mouseDownPos.current) {
                const dx = e.clientX - mouseDownPos.current.x;
                const dy = e.clientY - mouseDownPos.current.y;
                if (dx * dx + dy * dy > 25) return; // 5px threshold
            }
            const instance = chartRef.current?.getEchartsInstance?.();
            if (!instance) return;
            const rect = wrapper.getBoundingClientRect();
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;
            const inGrid = instance.containPixel({ gridIndex: 0 }, [offsetX, offsetY]);

            // The Special Study, pinned by this click.
            //
            // Handled BEFORE the bar-selection callback and WITHOUT returning, so the click still
            // does everything it did before. The card is an addition to the gesture rather than a
            // mode that takes it over: a click that silently stopped selecting bars would break the
            // drawing tools for anyone who left this floater switched on.
            if (isSpecialRef.current) {
                if (!inGrid) {
                    // A click on the canvas background closes it - one of the three ways out.
                    setFloaterPos(p => ({ ...p, visible: false }));
                    floaterDateRef.current = null;
                } else {
                    const point = instance.convertFromPixel({ gridIndex: 0 }, [offsetX, offsetY]);
                    const dates = barDatesRef.current;
                    const idx = point ? Math.round(point[0]) : -1;
                    if (dates && idx >= 0 && idx < dates.length) {
                        const px = instance.convertToPixel({ xAxisIndex: 0 }, idx);
                        floaterDateRef.current = dates[idx];
                        // Cleared first, so the previous bar's answer cannot sit under the new bar
                        // while its request is in flight - which reads as the study having answered
                        // about a bar it has not looked at.
                        setFloaterData(null);
                        setFloaterPos({
                            x: (typeof px === 'number' ? px : offsetX) + 16, y: 10, visible: true,
                        });
                        if (fetchFloaterRef.current) fetchFloaterRef.current(dates[idx]);
                    }
                }
            }

            const cb = onBarSelectRef.current;
            if (!cb || !inGrid) return;
            const pointInGrid = instance.convertFromPixel({ gridIndex: 0 }, [offsetX, offsetY]);
            if (pointInGrid) {
                const dataIndex = Math.round(pointInGrid[0]);
                const price = pointInGrid[1];
                const dates = barDatesRef.current;
                if (dates && dataIndex >= 0 && dataIndex < dates.length) {
                    /*
                     * THE PRICE EQUIVALENT OF A FEW PIXELS, measured here because only the chart
                     * knows its own scale.
                     *
                     * The caller splits a click into "on a candle" and "in the air around it",
                     * and a doji is a mathematical line with zero clickable height — so the test
                     * needs slack, and slack expressed in PRICE would be most of a 1993 candle
                     * and invisible against a 2026 one. Two conversions per click.
                     */
                    const padded = instance.convertFromPixel(
                        { gridIndex: 0 }, [offsetX, offsetY + BAR_HIT_PADDING_PX]);
                    const priceTolerance = (padded && Number.isFinite(padded[1]))
                        ? Math.abs(price - padded[1]) : 0;
                    // A FOURTH ARGUMENT AS AN OBJECT, so the next field to travel with a click
                    // does not reshuffle three positional ones at every call site.
                    cb(dates[dataIndex], dataIndex, price, { priceTolerance });
                }
            }
        };
        /**
         * Right-click, or shift-click, opens the spiral inspection menu on that bar.
         *
         * Two gestures for one action on purpose. Right-click is the discoverable one, but it is
         * also the gesture a user needs for the browser's own menu, and on a trackpad without a
         * second button it is awkward - so shift-click is offered as an equal alternative rather
         * than a fallback. preventDefault is called only when a bar was actually resolved, so a
         * right-click on the chart's margins still gets the normal browser menu.
         */
        const onContextMenu = (e) => {
            const cb = onBarContextMenuRef.current;
            if (!cb) return;
            const bar = barAtEvent(e);
            if (!bar) return;
            e.preventDefault();
            cb({ ...bar, clientX: e.clientX, clientY: e.clientY });
        };
        const onShiftClick = (e) => {
            if (!e.shiftKey) return;
            const cb = onBarContextMenuRef.current;
            if (!cb) return;
            const bar = barAtEvent(e);
            if (!bar) return;
            // Stops the plain-click handler above from also treating this as a bar selection, which
            // would run a study and open a menu from one gesture.
            e.preventDefault();
            e.stopPropagation();
            cb({ ...bar, clientX: e.clientX, clientY: e.clientY });
        };

        wrapper.addEventListener('mousedown', onDown);
        // Capture phase, so the shift-click is intercepted before the selection handler sees it.
        wrapper.addEventListener('click', onShiftClick, true);
        wrapper.addEventListener('click', onClick);
        wrapper.addEventListener('contextmenu', onContextMenu);
        return () => {
            wrapper.removeEventListener('mousedown', onDown);
            wrapper.removeEventListener('click', onShiftClick, true);
            wrapper.removeEventListener('click', onClick);
            wrapper.removeEventListener('contextmenu', onContextMenu);
        };
    }, [barAtEvent]);

    // --- Generic Grid Click Handler ---


 

    // --- Chart Colors Memo ---
    const currentChartColors = useMemo(() => {
        const selectedThemeColors = theme.palette.mode === 'dark' ? darkThemeColors : lightThemeColors;
        return { ...selectedThemeColors, ...propsChartColors };
    }, [propsChartColors, theme.palette.mode]);

    // --- Study mode refs (needed by both the memo short-circuit and the patch effect) ---
    const studyModeRef = useRef(false);
    studyModeRef.current = !!props.studyMode;
    const barDataRef = useRef(propsBarData);
    barDataRef.current = propsBarData;
    // Mirrored into a ref because the study-reveal patch below depends only on propsBarData, on
    // purpose — adding chartType to its deps would make every view change run the reveal path.
    const chartTypeRef = useRef(chartType);
    chartTypeRef.current = chartType;
    const studyBarCountRef = useRef(0);

    // --- Final Chart Options Memo (CRITICAL LOGIC) ---
    const cachedChartOptionsRef = useRef(null);
    /**
     * The palette the cached options were built with.
     *
     * <h2>What this closes</h2>
     *
     * The short-circuit below returns the cached option object during study mode, so a change
     * that is neither a new bar nor a mode change never reaches the chart — and a COLOUR is
     * exactly that. A reader who opened the configuration dialog while a study or a simulate
     * run was on screen picked a colour, saved, and watched nothing happen: for a first-time
     * pick that is indistinguishable from the role not being bound at all, which is how it was
     * reported.
     *
     * <p>Identity rather than a deep compare: `readWorkspaceColors` builds a fresh object on
     * every change and `App` holds it in state, so a new object IS a change and an unchanged
     * one is the same reference. A deep compare would be slower than the rebuild it guards.
     */
    const cachedPaletteRef = useRef(workspaceColors);
    const finalChartOptions = useMemo(() => {
        // Short-circuit: during study mode, skip expensive recomputation.
        // The study reveal useEffect handles the cheap incremental patch separately.
        //
        // A PALETTE CHANGE BUSTS IT, because nothing else would carry one through: the patch
        // effect this defers to writes price data onto two series by id and touches no colour.
        if (studyModeRef.current && cachedChartOptionsRef.current && studyBarCountRef.current > 0
            && cachedPaletteRef.current === workspaceColors) {
            return cachedChartOptionsRef.current;
        }

        // --- 1. Calculate Initial Zoom/View Window ---
        const realBars = propsBarData?.realBarCount || propsBarData?.ohlc?.length || 0;
        const totalBars = propsBarData?.ohlc?.length || 0;
        const extensionBars = Math.max(0, totalBars - realBars);

        let initialStart = 0;
        let initialEnd = Math.min(totalBars - 1, realBars + FUTURE_VIEW_BARS - 1);
        if (extensionBars >= FUTURE_VIEW_BARS) {
            initialEnd = realBars + FUTURE_VIEW_BARS - 1;
        } else {
            initialEnd = totalBars - 1;
        }
        
        let zoomStart = null, zoomEnd = null;
        if (zoomRef.current.startValue !== null && zoomRef.current.endValue !== null) {
            zoomStart = zoomRef.current.startValue;
            zoomEnd = zoomRef.current.endValue;
        } else {
            zoomStart = initialStart;
            zoomEnd = initialEnd;
        }


        // --- 2. Prepare Vertical and Cycle MarkLines ---
        const cycleLineOverlays = (overlays || []).filter(ovr => ovr.type === 'cycleLines');
        const extraMarkLines = cycleLineOverlays.flatMap(cycleLinesConfig => {
            const lines = [];
            const bar1Index = propsBarData.dates.indexOf(cycleLinesConfig.bar1Date);
            const bar2Index = propsBarData.dates.indexOf(cycleLinesConfig.bar2Date);

            if (bar1Index !== -1 && bar2Index !== -1) {
                const barDiff = Math.abs(bar2Index - bar1Index);
                if (barDiff > 0) {
                    lines.push({ xAxis: bar1Index, lineStyle: { type: 'dotted', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                    lines.push({ xAxis: bar2Index, lineStyle: { type: 'dotted', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });

                    const ratio = cycleLinesConfig.displayType === 'fib_ratio_length' ? parseFloat(cycleLinesConfig.fibRatio) : 1;

                    if (cycleLinesConfig.distanceUnit === 'days') {
                        const date1 = new Date(cycleLinesConfig.bar1Date);
                        const date2 = new Date(cycleLinesConfig.bar2Date);
                        const dayDiff = Math.abs(Math.round((date2 - date1) / (1000 * 60 * 60 * 24)));
                        const projectedDayInterval = Math.round(dayDiff * ratio);

                        if (projectedDayInterval <= 0) return [];

                        const laterDate = date1 > date2 ? date1 : date2;
                        const earlierDate = date1 < date2 ? date1 : date2;

                        // Project forward from the later date
                        let lastFoundDateFwd = laterDate;
                        while (lastFoundDateFwd.getTime() < new Date(propsBarData.dates[propsBarData.dates.length - 1]).getTime()) {
                            const nextProjectedDate = new Date(lastFoundDateFwd);
                            nextProjectedDate.setDate(nextProjectedDate.getDate() + projectedDayInterval);
                            
                            const nextIndex = findClosestTradingDayIndex(nextProjectedDate, propsBarData.dates);

                            if (nextIndex !== -1 && new Date(propsBarData.dates[nextIndex]) > lastFoundDateFwd) {
                                lines.push({ xAxis: nextIndex, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                                lastFoundDateFwd = new Date(propsBarData.dates[nextIndex]);
                            } else {
                                break;
                            }
                        }

                        // Project backward from the earlier date
                        let lastFoundDateBwd = earlierDate;
                        while (lastFoundDateBwd.getTime() > new Date(propsBarData.dates[0]).getTime()) {
                            const nextProjectedDate = new Date(lastFoundDateBwd);
                            nextProjectedDate.setDate(nextProjectedDate.getDate() - projectedDayInterval);

                            const nextIndex = findClosestTradingDayIndex(nextProjectedDate, propsBarData.dates);

                            if (nextIndex !== -1 && new Date(propsBarData.dates[nextIndex]) < lastFoundDateBwd) {
                                lines.push({ xAxis: nextIndex, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                                lastFoundDateBwd = new Date(propsBarData.dates[nextIndex]);
                            } else {
                                break;
                            }
                        }
                    } else { // 'bars' mode
                        const projectedDiff = Math.round(barDiff * ratio);
                        if (projectedDiff <= 0) return []; // Prevent infinite loop

                        if (projectedDiff > 0) {
                            // Project lines to the right
                            for (let i = bar2Index + projectedDiff; i < propsBarData.dates.length; i += projectedDiff) {
                                lines.push({ xAxis: i, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                            }

                            // Project lines to the left
                            for (let i = bar1Index - projectedDiff; i >= 0; i -= projectedDiff) {
                                lines.push({ xAxis: i, lineStyle: { type: 'solid', color: cycleLinesConfig.color || 'red', width: cycleLinesConfig.lineWidth || 1 } });
                            }
                        }
                    }
                }
            }
            return lines;
        });

        if (!propsBarData || !propsBarData.dates || propsBarData.dates.length === 0) {
            return {};
        }

        // horizontalLine overlays are now handled by chartDataProcessor.getMarkings,
        // so we only need cycle-line extraMarkLines here.

        // --- Determine Axis Visibility ---
        const hasDeclination = (overlays || []).some(o => o.type === 'planetaryLine' && o.mode === 'declination');
        /**
         * WHETHER THIS INSTRUMENT HAS A VOLUME FEED AT ALL.
         *
         * <p>TwelveData sends no {@code volume} field for metals or currency pairs, so every one
         * of their bars is stored with volume 0 and the pane drew a flat line along its own
         * baseline. That wasted a tenth of the chart's height and, worse, read as a broken feed
         * rather than as an instrument that has no volume.
         *
         * <p>True for anything not catalogued — an unknown symbol is an equity and keeps the pane
         * it has always had.
         */
        const showVolume = symbolReportsVolume(symbol);

        const hasVolumeLongitude = (overlays || []).some(o => o.type === 'planetaryLine' && o.panel === 'volume');
        const hasAstroGraph = (overlays || []).some(o => o.type === 'astroGraphLine');
        // No explicit axis bounds — let ECharts auto-scale with scale:true.
        // Keep astroSeriesRef for diagnostics only.
        if (hasAstroGraph && propsBarData && propsBarData.dates) {
            const agOverlay = (overlays || []).find(o => o.type === 'astroGraphLine');
            if (agOverlay && Array.isArray(agOverlay.data)) {
                const valMap = {};
                agOverlay.data.forEach(pt => {
                    const v = pt.value;
                    if (typeof v === 'number' && isFinite(v)) {
                        valMap[(pt.date || '').substring(0, 10)] = v;
                    }
                });
                astroSeriesRef.current = propsBarData.dates.map(d => {
                    const v = valMap[(d || '').substring(0, 10)];
                    return (typeof v === 'number' && isFinite(v)) ? v : null;
                });
            }
        } else {
            astroSeriesRef.current = null;
        }
        const hasCharacteristicSpeed = (overlays || []).some(o => o.type === 'characteristicLine' && o.characteristicType === 'speed' && o.panel !== 'main');
        const hasCharacteristicDistPct = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'distance_percent' || o.characteristicType === 'distance_percent_diff') && o.panel !== 'main');
        const hasCharacteristicDiffDeg = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'longitude_diff' || o.characteristicType === 'declination_diff') && o.panel !== 'main');
        const hasCharacteristicSpeedMain = (overlays || []).some(o => o.type === 'characteristicLine' && o.characteristicType === 'speed' && o.panel === 'main');
        const hasCharacteristicDistPctMain = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'distance_percent' || o.characteristicType === 'distance_percent_diff') && o.panel === 'main');
        const hasCharacteristicDiffDegMain = (overlays || []).some(o => o.type === 'characteristicLine' && (o.characteristicType === 'longitude_diff' || o.characteristicType === 'declination_diff') && o.panel === 'main');

        /* ── RECLAIMING THE BOTTOM PANEL WHEN THERE IS NOTHING IN IT ────────────────────────
         *
         * GRID 1 IS NOT "THE VOLUME GRID". It is the shared bottom panel, and Volume is only one
         * of the things drawn in it — RSI, MACD, planetary longitude and the four characteristic
         * series all live there too. So hiding volume must NOT collapse the panel on its own, or
         * an FX chart with RSI switched on would lose the RSI as well.
         *
         * The panel is reclaimed only when it would otherwise be EMPTY. That is the honest reading
         * of "don't waste the space": a flat zero line is waste, an RSI is not.
         */
        const bottomPanelHasContent = showVolume
            || showRSI
            || showMACD
            || hasVolumeLongitude
            || hasCharacteristicSpeed
            || hasCharacteristicDistPct
            || hasCharacteristicDiffDeg;

        /**
         * The divider the grids are actually laid out with.
         *
         * <p>At 92.5 the arithmetic below gives the main grid {@code 5%..95%} and leaves the
         * bottom grid a height of exactly ZERO. Collapsing it rather than deleting it is
         * deliberate: grid 1, its x-axis and its y-axes keep their indices, so every series that
         * names {@code xAxisIndex: 1} still resolves, the {@code dataZoom} entries covering
         * {@code [0, 1]} stay valid, and the linked crosshair keeps the two axes it links. Removing
         * a grid would renumber everything downstream of it.
         *
         * <p>Derived ONCE and used everywhere `dividerPercent` was — the grid array AND both
         * `gridRect` calls. The comment on `tradeLabelBounds` already says why: two independent
         * copies of these numbers is how a clamp comes to hold a card inside a rectangle the plot
         * no longer occupies, which looks exactly like the clamp not working.
         */
        const BOTTOM_PANEL_COLLAPSED_DIVIDER = 92.5;
        const effectiveDividerPercent = bottomPanelHasContent
            ? dividerPercent
            : BOTTOM_PANEL_COLLAPSED_DIVIDER;

        // --- 4. Process Chart Data (Calling External Utility) ---
        const { 
            mainSeries, 
            maSeries, 
            trendLineSeries, 
            planetaryLineSeries,
            volumeSeries,
            allFormattedDates,
            // THE OPEN TRADE CARDS, as content. They are not a series -- the chart draws them as
            // draggable `graphic` elements, which needs pixel positions and therefore the live
            // instance. Stashed on a ref just below and placed by `renderTradeCards`.
            tradeCards,
        } = processChartData(
            propsBarData,
            currentChartColors,
            overlays,
            viewMode,
            chartType,
            showPatterns,
            null, // patternVisibility removed
            maConfigs,
            {
                // The Bar Floater's OHLCV mode means "price data only", and an astrological hover
                // card is exactly what that excludes. Kept as its own flag because it is a
                // narrower statement than the whitelist below and the two are set by different
                // controls.
                // Short bar mode forces this on regardless of the floater selection. The preset
                // must not expose anything astrological, and the floater selection is a separate
                // control that a session could have left on planets or aspects before switching.
                revealedUpToDate,
                lastTradedDate,
                // ⚠ AND NOT FOR AN ADMINISTRATOR. See `isAdmin` above: this boolean is true for
                // everybody out of the box, so without the exemption an admin-only card is
                // invisible to the only audience it has.
                suppressAstroTooltips: !isAdmin && (shortBarMode || barFloaterMode === 'ohlcv'),
                // THE PRESET, on its own. `suppressAstroTooltips` folds two unrelated rules into
                // one boolean - a product constraint (short bar exposes nothing astrological) and a
                // reader's preference (the floater is showing OHLCV) - and `barFloaterMode`
                // defaults to 'ohlcv', so that boolean is TRUE for everybody out of the box.
                //
                // Anything keyed on it is therefore off by default, which is right for a planetary
                // hover card and wrong for a study whose card is a date. Passed separately so the
                // two can be asked apart.
                shortBarMode,
                // Passed through so the processor can exempt an admin from the short-bar tooltip
                // lock as well. The lock on the PRESET is unchanged -- it still hides the controls
                // and the marks for everybody; this is only about whether a card may open.
                isAdmin,
                // THE CUSTOMER'S CONFIGURED COLOURS, resolved by role inside the processor. Passed
                // through as data rather than applied to the overlays, so a Save re-renders and
                // every already-drawn band and arrow takes the new colour without any study being
                // re-run - which is what "immediately" has to mean for a chart that took a minute
                // to compute.
                workspaceColors,
                // THE OPEN DECORATORS, and the rectangle they must stay inside.
                //
                // The bounds are derived from the SAME numbers the `grid` below is configured with
                // -- 4% / 30px / 5% / dividerPercent -- via one helper. Two independent copies of
                // those is how a clamp comes to hold a card inside a rectangle the plot no longer
                // occupies, which looks exactly like the clamp not working.
                tradeDecorators: openDecorators,
                // THE YEARLY SUMMARY PLAQUES. Produced beside the trade cards and in the same
                // list, so one placer and one drag mechanism serve both.
                showYearlySummaries,
                tradeLabelBounds: chartPixelSize
                    ? gridRect({ ...chartPixelSize, dividerPercent: effectiveDividerPercent })
                    : null,
                // THE STRICT WHITELIST. Only the Integral Delta trade and pending markers keep a
                // hover card of their own; the candlestick is served by the global formatter,
                // which already returns '' for anything else. Everything not on the list leaves
                // with its tooltip switched off.
                restrictTooltips: true,
            }
        );

        // Handed to the imperative placer below. Writing a ref during render is a side effect, so
        // it is worth saying why it is safe here: the value is DERIVED from this render's inputs
        // and the assignment is idempotent, so a double-invoked render writes the same thing
        // twice. The alternative -- returning it from the memo -- changes what every consumer of
        // `finalChartOptions` receives, for one field only this file reads.
        tradeCardsRef.current = Array.isArray(tradeCards) ? tradeCards : [];

        // Merge cycle-line extraMarkLines into mainSeries markLine (preserve existing from processChartData)
        if (mainSeries && extraMarkLines.length > 0) {
            mainSeries.markLine = mainSeries.markLine || {};
            const existing = mainSeries.markLine.data || [];
            mainSeries.markLine.data = [...existing, ...extraMarkLines];
            mainSeries.markLine.symbol = 'none'; 
            mainSeries.markLine.silent = true; 
        }

        // --- 4b. AstroGraph Pattern Prediction Lines ---
        const patternOverlay = (overlays || []).find(o => o.type === 'astroPatternLines');
        if (patternOverlay && Array.isArray(patternOverlay.patterns) && mainSeries) {
            const patternMarkLines = [];
            const futureMarkPoints = [];
            for (const pat of patternOverlay.patterns) {
                const dateStr = (pat.date || '').substring(0, 10);
                const idx = propsBarData.dates.findIndex(d => d.substring(0, 10) === dateStr);
                if (idx === -1) continue;

                const isFuture = pat.region === 'future';
                const prediction = pat.prediction || '';
                const shape = pat.shape || '';

                const isBearish = shape === 'Peak' || shape === 'DoubleTop'
                    || shape === 'ZeroCrossDown' || shape === 'SlopeReversalDown'
                    || prediction === 'Bearish';
                const isBullish = shape === 'Trough' || shape === 'DoubleBottom'
                    || shape === 'ZeroCrossUp' || shape === 'SlopeReversalUp'
                    || prediction === 'Bullish';

                const color = isBearish ? '#e53935' : isBullish ? '#43a047' : '#9e9e9e';
                const arrow = isBearish ? '\u2193' : isBullish ? '\u2191' : '\u2013';
                const shortShape = shape.replace('SlopeReversal', 'Slope').replace('ZeroCross', 'ZX').replace('Double', 'Dbl');

                // Vertical line for both historical and future
                patternMarkLines.push({
                    xAxis: idx,
                    lineStyle: {
                        type: isFuture ? 'dashed' : 'solid',
                        color,
                        width: isFuture ? 2 : 1,
                    },
                    label: {
                        show: !isFuture,
                        formatter: `${shortShape} ${arrow}`,
                        position: 'start',
                        fontSize: 9,
                        color,
                        rotate: 90,
                        offset: [0, 4],
                    },
                });

                // Floating annotation for future predictions only
                if (isFuture) {
                    const hitRate = pat.hitRate != null ? Math.round(pat.hitRate) : null;
                    const avgChg = pat.avgChange10d != null ? pat.avgChange10d : null;
                    const offsetLbl = pat.bestOffsetLabel || '';
                    const flatLen = pat.flatLength || 1;
                    let floaterText = `${shortShape} ${arrow}`;
                    if (prediction) floaterText += `\n${prediction}`;
                    if (hitRate != null) floaterText += `\nHit: ${hitRate}%`;
                    if (avgChg != null) floaterText += `\nAvg 10d: ${avgChg > 0 ? '+' : ''}${avgChg}%`;
                    if (offsetLbl) floaterText += `\nTypical: ${offsetLbl}`;
                    if (flatLen > 1) floaterText += `\nFlat: ${flatLen} bars`;

                    // Place annotation near the top for bearish, near the bottom for bullish
                    const ohlc = propsBarData.ohlc;
                    const yVal = isBearish
                        ? (ohlc[idx] ? Math.max(ohlc[idx][3], ohlc[idx][0]) : 0)
                        : (ohlc[idx] ? Math.min(ohlc[idx][2], ohlc[idx][1]) : 0);

                    futureMarkPoints.push({
                        coord: [idx, yVal],
                        symbol: isBearish ? 'triangle' : 'triangle',
                        symbolRotate: isBearish ? 180 : 0,
                        symbolSize: 14,
                        itemStyle: { color },
                        label: {
                            show: true,
                            formatter: floaterText,
                            position: isBearish ? 'top' : 'bottom',
                            fontSize: 10,
                            fontWeight: 'bold',
                            color,
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            borderColor: color,
                            borderWidth: 1,
                            borderRadius: 4,
                            padding: [4, 6],
                            lineHeight: 14,
                        },
                    });
                }
            }
            if (patternMarkLines.length > 0) {
                mainSeries.markLine = mainSeries.markLine || {};
                const existing = mainSeries.markLine.data || [];
                mainSeries.markLine.data = [...existing, ...patternMarkLines];
                mainSeries.markLine.symbol = 'none';
                mainSeries.markLine.silent = true;
            }
            if (futureMarkPoints.length > 0) {
                mainSeries.markPoint = mainSeries.markPoint || {};
                const existingPts = mainSeries.markPoint.data || [];
                mainSeries.markPoint.data = [...existingPts, ...futureMarkPoints];
                mainSeries.markPoint.animation = false;
            }
        }

        // --- 5. RSI Setup ---
        let computedRSI = [];
        
        if (showRSI) {
            // Logic to compute RSI locally if propRsiData is not available
            if (!Array.isArray(propRsiData) || propRsiData.length === 0) {
                // FIX: Call the function that was defined outside of the useMemo
                computedRSI = computeRSI(propsBarData.ohlc);
            } else {
                computedRSI = propRsiData;
            }
        }

        // Tag candlestick + volume with stable IDs for targeted study-mode patching
        if (mainSeries) mainSeries.id = '__candle__';
        if (volumeSeries) volumeSeries.id = '__volume__';

        // Only include valid, non-null series in allSeries
        let allSeries = [
            mainSeries, 
            ...(Array.isArray(maSeries) ? maSeries : [maSeries]), 
            ...trendLineSeries, 
            ...(Array.isArray(planetaryLineSeries) ? planetaryLineSeries : [planetaryLineSeries]), 
            // OMITTED, not emptied: a bar series with no data still claims its y-axis and
            // still draws the axis line and labels down the left of the panel.
            showVolume ? volumeSeries : null
        ].filter(s => s && typeof s === 'object' && s.type);

        // Add RSI series if present
        if (showRSI && computedRSI.length > 0) {
            allSeries.push({
                name: 'RSI',
                type: 'line',
                data: computedRSI,
                xAxisIndex: 1,
                yAxisIndex: 1,
                smooth: true,
                symbol: 'none',
                // A POINT THICKER, and the colour comes from the palette rather than from a
                // literal here. `resolveColor` prefers the reader's own `rsiLine` and falls
                // back to the active theme's, so an unconfigured install is unchanged but for
                // the width.
                lineStyle: {
                    color: resolveColor(workspaceColors, 'rsiLine', currentChartColors.rsiLine),
                    width: RSI_LINE_WIDTH,
                },
                showSymbol: false,
                z: 10,
                markLine: {
                    data: [
                        { yAxis: 30, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '30' } },
                        { yAxis: 50, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '50' } },
                        { yAxis: 70, lineStyle: { color: '#888', width: 1, type: 'dashed' }, label: { show: true, formatter: '70' } },
                    ],
                    symbol: 'none',
                    lineStyle: { color: '#888', width: 1, type: 'dashed' },
                    label: { show: true }
                }
            });
        }

        if (showMACD && macdData && macdData.macd && macdData.signal && macdData.histogram) {
            allSeries.push({
                name: 'MACD Histogram',
                type: 'bar',
                data: macdData.histogram.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                itemStyle: { color: '#90caf9' },
                barWidth: 6,
                /*
                 * OVERLAPPED, NOT SIDE BY SIDE -- which is what keeps the volume bars readable.
                 *
                 * ECharts lays out every BAR series on one category axis as a group, dividing
                 * each category's width between them. This histogram shares xAxis 1 with the
                 * volume bars, so switching MACD on halved them: measured on SPY at a hundred-bar
                 * zoom, a volume bar went from 21.62px to 9.67px and the volume trend became
                 * impossible to read.
                 *
                 * `-100%` starts this bar where the previous series' bar starts, so the two
                 * occupy the same slot rather than sharing it and each keeps its own width. They
                 * are on different y axes -- volume on 2, MACD on 3 -- so they were never
                 * comparable side by side anyway; the adjacency was an artefact of the layout
                 * rather than a reading.
                 */
                barGap: '-100%',
                z: 8
            });
            allSeries.push({
                name: 'MACD',
                type: 'line',
                data: macdData.macd.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                lineStyle: { color: '#388e3c', width: 1 },
                showSymbol: false,
                z: 9
            });
            allSeries.push({
                name: 'MACD Signal',
                type: 'line',
                data: macdData.signal.map(pt => pt.value),
                xAxisIndex: 1,
                yAxisIndex: 3, 
                lineStyle: { color: '#d32f2f', width: 1, type: 'dashed' },
                showSymbol: false,
                z: 9
            });
        }

        // Chart options object
        const result = {
            // TRANSPARENT, deliberately, on both themes: the canvas shows the container behind it,
            // so the workspace background is set once in CSS and the chart cannot disagree with
            // the page it sits on. Painting it here would give the chart its own idea of the
            // background and leave a seam at the edge of the canvas on any size mismatch.
            backgroundColor: currentChartColors.backgroundColor,
            graphic: watermarkOverride?.hidden ? [] : [{
                type: 'text',
                // SILENT, and this was a real defect rather than a precaution.
                //
                // A `graphic` element is INTERACTIVE by default, and this one is a 250px string
                // across the middle of the plot. It was swallowing every click that landed on it
                // -- ECharts reported `componentType: 'graphic'` and the series underneath never
                // saw the gesture. Found while wiring the trade-line click: clicking a line in the
                // centre of the chart did nothing at all, while the identical line near the edges
                // worked.
                //
                // It predates this feature and is not specific to it: anything click-driven over
                // the middle of the price panel was affected. A watermark is decoration and must
                // never be a hit target.
                silent: true,
                left: 'center',
                top: '35%',
                style: {
                    text: watermarkOverride?.text || resolvedWatermark || DEFAULT_WATERMARK,
                    font: `bold ${watermarkOverride?.fontSize || 250}px sans-serif`,
                    /*
                     * ⚠ PER CANVAS. The single `WATERMARK_FILL` was tuned for the dark theme and
                     * composites to #fcfcfc over white -- 1.02:1, which is the "watermark
                     * disappears on the light theme" report, measured. See `watermarkFillFor`.
                     */
                    fill: watermarkFillFor(theme.palette.mode),
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                }
            }],
            /**
             * The crosshair, and the one place ECharts will read `link` from.
             *
             * This is a two-grid chart: grid 0 is the price panel, grid 1 carries RSI, MACD and
             * Volume. Each grid is its own coordinate system with its own axis pointer, so without
             * a link they track independently and hovering a candle leaves the indicator pane's
             * crosshair wherever it was.
             *
             * `link` used to sit in `tooltip.axisPointer`, where it does nothing. Verified in
             * echarts 5.6 rather than assumed — `modelHelper.collect` reads it as
             * `globalAxisPointerModel.get('link', true)`, i.e. from THIS component and with the
             * parent lookup disabled, and `makeAxisPointerModel` copies only
             * `type, snap, lineStyle, shadowStyle, label, animation, animationDurationUpdate,
             * animationEasingUpdate, z` across from the tooltip. `link` is not on that list, so it
             * was read from a place it is never looked for: no error, no warning, no linking.
             *
             * What DID work from over there is `type: 'cross'` — the same function shows that a
             * cross type alone activates the pointers even though the tooltip triggers on 'item'
             * (`if (triggerAxis || cross)`). So the crosshair was drawn but never shared, which is
             * exactly the reported symptom.
             *
             * `animation: false` because a crosshair is a cursor, not a chart element. The default
             * eases it towards the pointer, which reads as lag when tracking bar to bar.
             */
            axisPointer: {
                link: [{ xAxisIndex: 'all' }],
                animation: false,
            },
            tooltip: {
                trigger: 'item',
                axisPointer: {
                    // No `link` here — see the axisPointer component above. Adding one back is
                    // harmless but inert, which is worse than useless: it reads as configured.
                    type: 'cross',
                    animation: false,
                    crossStyle: {
                        color: currentChartColors.crosshairColor
                    }
                },
                formatter: (params) => {
                    if (!params || !params.name) return '';
                    // THE TOOLTIP LOCK. In short bar mode nothing astrological may reach the
                    // screen, so anything that is not price data is refused here outright - before
                    // any series-level formatter gets a chance, and without depending on the
                    // whitelist further down having been applied.
                    //
                    // Belt and braces on purpose. The whitelist in processChartData already
                    // switches those series' tooltips off, but that is one flag in one call site;
                    // this is the last gate every hover passes through, and a security rule that
                    // exists in exactly one place is a rule that a refactor can delete by accident.
                    if (shortBarModeRef.current && params.seriesType !== 'candlestick') return '';
                    // For candlestick series, show OHLCV with weekday
                    if (params.seriesType === 'candlestick' && params.data && Array.isArray(params.data)) {
                        const dateStr = params.name;
                        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        let weekday = '';
                        let d;
                        // Handle YYYY-MM-DD format
                        const isoDash = dateStr.split('-');
                        const slashParts = dateStr.split('/');
                        if (isoDash.length === 3 && isoDash[0].length === 4) {
                            d = new Date(parseInt(isoDash[0]), parseInt(isoDash[1]) - 1, parseInt(isoDash[2]));
                        } else if (slashParts.length === 3) {
                            d = new Date(parseInt(slashParts[2]), parseInt(slashParts[0]) - 1, parseInt(slashParts[1]));
                        }
                        if (d && !isNaN(d.getTime())) weekday = days[d.getDay()];
                        // params.data = [categoryIndex, open, close, low, high]
                        const open  = params.data[1];
                        const close = params.data[2];
                        const low   = params.data[3];
                        const high  = params.data[4];
                        const vol = propsBarData?.volumes?.[params.dataIndex];
                        const row = (label, val) => `<tr><td style="padding-right:8px">${label}:</td><td style="text-align:right">${Number(val).toFixed(2)}</td></tr>`;
                        // THE INDICATORS, after a blank line, and only the ones that have a value
                        // at this bar. Read from the SAME arrays the two series are drawn from --
                        // `computedRSI` is what the RSI line plots and `macdData.macd` is what the
                        // MACD line plots -- so a number in the tooltip is the number on the
                        // chart rather than a second calculation that could disagree with it.
                        //
                        // Empty when an indicator is switched off: `computedRSI` is only populated
                        // when `showRSI`, and the MACD series is only built when `showMACD`, so
                        // quoting either would be a reading of something the reader cannot see.
                        const indicators = indicatorRows(params.dataIndex, {
                            rsi: computedRSI,
                            macd: showMACD && macdData && macdData.macd
                                ? macdData.macd.map(pt => (pt ? pt.value : null)) : null,
                            decimals: macdDecimals(close),
                        });
                        return `<b>${weekday} ${dateStr}</b>`
                            + `<table style="border-collapse:collapse;margin-top:4px">`
                            + row('Open', open)
                            + row('High', high)
                            + row('Low', low)
                            + row('Close', close)
                            + (vol != null ? `<tr><td style="padding-right:8px">Volume:</td><td style="text-align:right">${Number(vol).toLocaleString()}</td></tr>` : '')
                            + indicators
                            + `</table>`;
                    }
                    return '';
                },
                /*
                 * THE TOOLTIP'S OWN SURFACE, which was never set.
                 *
                 * <p>`tooltipBackground`, `tooltipBorder` and `tooltipColor` have been declared on
                 * both palettes since they were written and read by NOTHING, so the tooltip took
                 * ECharts' default: a white box with dark text. On the light theme that is very
                 * nearly the intended value and the omission was invisible; on charcoal it is a
                 * white card over a near-black chart, which is the one surface a reader hovers
                 * deliberately and the brightest thing on the screen.
                 *
                 * <p>A declared-and-unread key is worse than a missing one — it reads as
                 * configured. `chartTheme.test.js` now asserts every key in either palette is
                 * consumed somewhere, so the next one cannot sit unused.
                 */
                backgroundColor: currentChartColors.tooltipBackground,
                borderColor: currentChartColors.tooltipBorder,
                textStyle: { color: currentChartColors.tooltipColor },
            },
            grid: [
                { left: '4%', right: '30px', top: '5%', height: `${effectiveDividerPercent - 2.5}%` }, // Index 0: Main Chart
                { left: '4%', right: '30px', top: `${effectiveDividerPercent + 2.5}%`, height: `${Math.max(0, 95 - (effectiveDividerPercent + 2.5))}%` }, // Index 1: Unified Bottom Panel (height 0 when empty)
            ],
            xAxis: [
                // The main panel's x-axis draws NO pointer label. Both grids get an axis pointer and
                // both were labelling it, so the linked crosshair produced two identical date
                // badges a few pixels apart — one under the price panel, one under the indicator
                // panel. They are the same instant by construction (that is what `link` means), so
                // the second says nothing the first did not, and the pair straddling the boundary
                // reads as two different readings.
                //
                // The one kept is the LOWER one, on the indicator axis: it sits under the whole
                // stack rather than inside it, which is where a chart's date scale belongs. This
                // axis already hides its ordinary tick labels for the same reason.
                // THE DATE SCALE BELONGS TO WHICHEVER AXIS IS ACTUALLY AT THE BOTTOM.
                //
                // Normally that is axis 1, under the indicator panel, and axis 0 shows neither
                // labels nor a pointer badge -- both were labelling it once, which produced two
                // identical dates a few pixels apart straddling the panel boundary. They are the
                // same instant by construction (that is what `link` means), so the lower one wins.
                //
                // When the bottom panel is EMPTY it collapses to zero height, and "the lower one"
                // becomes axis 0. Without this flip the chart would keep its date labels pinned to
                // a grid of no height and lose its x scale entirely -- so exactly one axis shows
                // the dates and exactly one shows the crosshair badge, in both layouts.
                { type: 'category', data: allFormattedDates, gridIndex: 0, axisLine: { onZero: false }, splitLine: { show: false }, axisLabel: { show: !bottomPanelHasContent, color: currentChartColors.axisLabelColor }, axisTick: { show: !bottomPanelHasContent }, boundaryGap: true, axisPointer: { label: { show: !bottomPanelHasContent } } }, // Index 0 (Main)
                { type: 'category', data: allFormattedDates, gridIndex: 1, axisLine: { onZero: false }, axisTick: { show: bottomPanelHasContent }, splitLine: { show: false }, axisLabel: { show: bottomPanelHasContent, color: currentChartColors.axisLabelColor }, boundaryGap: true, axisPointer: { label: { show: bottomPanelHasContent } } }, // Index 1 (Bottom Panel)
            ],
            yAxis: [
                // 0: Main price panel
                // THE PRICE, and the only grid-0 axis that labels the crosshair. See `pointerLabel`
                // for what was covering it. The formatter is explicit so the label is the exact
                // price rather than ECharts' own rounding, which drops a tick on an FX pair.
                { scale: true, gridIndex: 0, splitArea: { show: true, areaStyle: { color: currentChartColors.splitAreaColors } }, splitLine: { show: true, lineStyle: { color: currentChartColors.gridColor } }, axisLabel: { color: currentChartColors.axisLabelColor, inside: false }, position: 'right', axisPointer: { label: { show: true, precision: 10, formatter: ({ value }) => formatAxisPrice(value) } } },
                // 1: RSI (right) for volume panel
                { min: 0, max: 100, scale: false, gridIndex: 1, splitNumber: 4, axisLabel: { show: true, color: currentChartColors.axisLabelColor, inside: false }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: true, lineStyle: { color: currentChartColors.paneLineColor, type: 'dotted' } }, position: 'right', name: 'RSI' },
                // 2: Volume (left) for volume panel
                { type: 'value', scale: true, gridIndex: 1, axisLabel: { show: true, color: currentChartColors.axisLabelColor }, axisLine: { show: true }, axisTick: { show: true }, splitLine: { show: true, lineStyle: { color: currentChartColors.paneLineColor, type: 'solid' } }, position: 'left', name: 'Volume', show: showVolume, ...pointerLabel(showVolume) },
                // 3: MACD yAxis (auto-scale, centered at 0) for volume panel
                { type: 'value', scale: true, gridIndex: 1, axisLabel: { show: true, color: '#388e3c' }, axisLine: { show: true }, axisTick: { show: true }, splitLine: { show: true, lineStyle: { color: currentChartColors.paneLineColor, type: 'dashed' } }, position: 'left', offset: 40, name: 'MACD', min: 'dataMin', max: 'dataMax', show: showMACD, ...pointerLabel(showMACD) },
                // 4: Dedicated planetary longitude axis
                { type: 'value', min: 0, max: 360, position: 'right', offset: 60, show: hasVolumeLongitude, name: 'Longitude (°)', gridIndex: 1, splitLine: { show: false }, ...pointerLabel(hasVolumeLongitude) },
                // 5: Dedicated Declination axis (Main Panel)
                { type: 'value', scale: true, position: 'right', offset: 60, show: hasDeclination, name: 'Declination', gridIndex: 0, splitLine: { show: false }, axisLabel: { formatter: '{value}°' }, ...pointerLabel(hasDeclination) },
                // 6: AstroGraph daily score axis (Main Panel, left side) — auto-scaled
                { type: 'value', scale: true, position: 'left', offset: 40, show: hasAstroGraph, gridIndex: 0, splitLine: { show: false }, axisLabel: { color: '#ff6600', formatter: (v) => Math.round(v) }, ...pointerLabel(hasAstroGraph) },
                // 7: Characteristics speed axis (Bottom Panel)
                { type: 'value', scale: true, gridIndex: 1, position: 'right', offset: 60, show: hasCharacteristicSpeed, name: 'Speed (°/day)', splitLine: { show: false }, ...pointerLabel(hasCharacteristicSpeed) },
                // 8: Characteristics distance axis (Bottom Panel)
                { type: 'value', min: 0, max: 100, scale: false, gridIndex: 1, position: 'right', offset: 140, show: hasCharacteristicDistPct, name: 'Distance (%)', splitLine: { show: false }, ...pointerLabel(hasCharacteristicDistPct) },
                // 9: Characteristics diff-deg axis (Bottom Panel)
                { type: 'value', min: 0, scale: true, gridIndex: 1, position: 'right', offset: 220, show: hasCharacteristicDiffDeg, name: 'Difference (°)', splitLine: { show: false }, ...pointerLabel(hasCharacteristicDiffDeg) },
                // 10: Characteristics speed axis (Main Panel)
                { type: 'value', scale: true, gridIndex: 0, position: 'left', offset: 80, show: hasCharacteristicSpeedMain, name: 'Speed (°/day)', splitLine: { show: false }, axisLabel: { color: '#F44336' }, ...pointerLabel(hasCharacteristicSpeedMain) },
                // 11: Characteristics distance axis (Main Panel)
                { type: 'value', min: 0, max: 100, scale: false, gridIndex: 0, position: 'left', offset: 120, show: hasCharacteristicDistPctMain, name: 'Distance (%)', splitLine: { show: false }, axisLabel: { color: '#4CAF50' }, ...pointerLabel(hasCharacteristicDistPctMain) },
                // 12: Characteristics diff-deg axis (Main Panel)
                { type: 'value', min: 0, scale: true, gridIndex: 0, position: 'left', offset: 160, show: hasCharacteristicDiffDegMain, name: 'Difference (°)', splitLine: { show: false }, axisLabel: { color: '#2196F3' }, ...pointerLabel(hasCharacteristicDiffDegMain) },
                // 13: THE RAIL AXIS — a fixed 0..1 scale on the price grid, drawn nowhere.
                //
                // The aspect rail puts a mark at the FOOT of the price panel for each flagged
                // bar, and it has to stay there at every zoom. A scatter needs a y in DATA
                // space, and the price axis rescales to whatever is in view — so a rail pinned
                // to the series' lowest low drifts off-screen the moment anyone zooms into a
                // later, higher stretch.
                //
                // This axis never moves: 0 is the grid floor and 1 the ceiling, whatever price
                // is doing. A point at 0.03 is 3% up from the bottom, always.
                //
                // APPENDED, never inserted. Every series above names its axis by INDEX, so
                // adding one anywhere but the end renumbers the lot — and the symptom would be
                // RSI drawn on the volume scale rather than an error.
                // ...and it must not LABEL the crosshair either. Drawn nowhere, but ECharts
                // still puts its 0..1 reading on the horizontal line -- at x=3283 against the
                // price label's x=3276, which is what was covering the price.
                { type: 'value', min: 0, max: 1, gridIndex: 0, show: false, splitLine: { show: false }, ...pointerLabel(false) },
            ],
            dataZoom: [
                { type: 'inside', xAxisIndex: [0, 1], filterMode: 'weakFilter', startValue: zoomStart, endValue: zoomEnd }, 
                { 
                    type: 'slider', 
                    xAxisIndex: [0, 1], 
                    filterMode: 'weakFilter',
                    startValue: zoomStart, 
                    endValue: zoomEnd, 
                    bottom: '0px', 
                    height: '20px', 
                    dataBackground: { lineStyle: { color: '#d3d3d3' }, areaStyle: { color: '#d3d3d3' } },
                    fillerColor: 'rgba(167,183,204,0.4)',
                    handleIcon: 'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
                    handleSize: '80%',
                    handleStyle: { color: '#fff', shadowBlur: 3, shadowColor: 'rgba(0, 0, 0, 0.6)', shadowOffsetX: 2, shadowOffsetY: 2 },
                },
                { type: 'inside', yAxisIndex: [0], start: 0, end: 100 },
                { type: 'slider', yAxisIndex: [0], right: 0, width: 25, start: 0, end: 100 },
            ],
            toolbox: {
                show: true,
                top: 0,
                right: 20,
                feature: {
                    dataZoom: {
                        show: true,
                        title: { zoom: 'Area Zoom', back: 'Zoom Out' },
                        tooltip: 'Click this and then highlight the area of the chart you want to zoom in on. You can do the same with mouse wheel rolling forward (and zoom out with the wheel rolling backwards)'
                    },
                },
                itemSize: 15,
                itemGap: 5,
                emphasis: { iconStyle: { borderColor: '#007bff' } },
                iconStyle: { borderColor: '#6c757d', borderWidth: 1 },
                tooltip: {
                    show: false,
                    formatter(param) { return `<div>${param.title}</div>`; },
                    backgroundColor: '#555',
                    textStyle: { color: 'white' }
                }
            },
            series: allSeries,
        };

        // ── THE LAST GATE ────────────────────────────────────────────────────────────────────
        //
        // Every producer has now had its say — the overlay series built by chartDataProcessor AND
        // the markPoint data this component pushes onto the candlestick series directly. This is
        // the only point at which all of them are in one object, which is why the guarantee is
        // enforced here rather than asked of each of them.
        //
        // It was asked of each of them, and that is how this bug survived a fix: the forward
        // confluence marks were corrected in gannConfluenceOverlay, and green and red triangles
        // kept appearing, because the ones on screen came from the candlestick-pattern predictions
        // a few hundred lines above — which never pass through chartDataProcessor at all.
        //
        // Nothing the market has not traded may be drawn as though it had.
        //
        // The boundary is derived from the BARS and the replay state, in physical column indices -
        // the coordinate space markPoints are actually expressed in. It used to be a date compared
        // against a lastRealDate prop, which failed twice over: markPoints carry `coord: [index]`
        // and no date at all, and in simulation mode barData keeps every one of its dates while
        // only the prices are masked, so that prop pointed at the end of history rather than the
        // edge of the replay.
        const { changed, boundary } = forceFutureMarkerStyle(result, {
            ohlc: propsBarData?.ohlc || [],
            dates: propsBarData?.dates || [],
            simulationBoundary,
        });
        if (Number.isFinite(boundary)) {
            // Logged with the BOUNDARY, not just the count. A sweep that matched nothing looks
            // identical to a sweep that never ran, and telling those apart from a screenshot is
            // exactly what cost the previous two attempts.
            console.debug(
                `[chart] future marker gate: boundary=${boundary}, forced=${changed}`);
        }

        cachedChartOptionsRef.current = result;
        cachedPaletteRef.current = workspaceColors;
        return result;
    // NOTE: zoomWindow intentionally NOT a dependency — zoom is handled by ECharts natively.
    // zoomRef.current is read inside the memo when it runs for other reasons.
    //
    // showCycleMarkers was also listed here and is not read by this memo, so it only forced a full
    // rebuild that produced identical options. Nothing else reads that state either — the
    // toggleCycleMarkers action flips it and getShowCycleMarkers is never called — so the toggle
    // currently has no renderer behind it. Removing the dependency changes no output; whether the
    // feature should be restored or dropped is a separate question.
    // shortBarMode is here for the same reason as barFloaterMode below: the option DEPENDS on it,
    // since it forces suppressAstroTooltips on. The formatter reads the mode through a ref rather
    // than this closure, so the lock survives even if a rebuild is ever missed - but the option
    // must still be rebuilt when the mode changes, or the astro series keep the tooltips they were
    // given before the switch.
    // barFloaterMode is here because the option now DEPENDS on it: it decides whether the
    // astrological marker tooltips are built at all. Without it in the array, switching the
    // floater to OHLCV would leave the previous option object in place and the cards would keep
    // appearing until some unrelated change happened to rebuild it.
    // simulationBoundary is a dependency because the final gate READS it: without it here, the
    // option would keep the boundary it was built with and stepping the replay forward would not
    // re-sweep - so a marker would stay gold after the bar under it had been revealed.
    // `symbol` is load-bearing here, not cosmetic: it decides whether this instrument has a
    // volume feed, and therefore whether the bottom panel is drawn at all. Omitted, the layout
    // would be whatever the FIRST symbol of the session needed -- so switching from SPY to XAUUSD
    // would keep an empty volume pane, and switching back would leave the price grid overlapping
    // the panel it had reclaimed.
    }, [propsBarData, overlays, currentChartColors, viewMode, chartType, dividerPercent, showPatterns, showRSI, propRsiData, showMACD, macdData, maConfigs, watermarkOverride, resolvedWatermark, barFloaterMode, shortBarMode, isAdmin, workspaceColors, simulationBoundary, revealedUpToDate, lastTradedDate, openDecorators, chartPixelSize, showYearlySummaries, symbol, theme.palette.mode]);


    /**
     * Places every open card and hands the result to the chart.
     *
     * <p>Imperative, and it has to be: the layout is in PIXELS, and the data-to-pixel mapping only
     * exists on the live instance. Called on mount, whenever the open set changes, and on every
     * zoom, pan and resize -- which is the cost of leaving the series system, and the reason
     * `layoutCards` is pure and tested on its own.
     */
    const renderTradeCards = useCallback(() => {
        const chart = chartRef.current?.getEchartsInstance?.();
        // Guarded like every other instance call in this file. On the first pass the instance is
        // not fully formed, and a test double may carry none of these methods.
        if (!chart || typeof chart.setOption !== 'function'
            || typeof chart.getWidth !== 'function'
            || typeof chart.convertToPixel !== 'function') return;

        const width = chart.getWidth();
        const height = chart.getHeight();
        if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return;

        const cards = tradeCardsRef.current || [];
        // THE DIVIDER THE OPTION WAS ACTUALLY BUILT WITH, read back off grid 0 rather than
        // recomputed from `dividerPercent`.
        //
        // The main grid grows to fill the chart when the bottom panel is empty — an FX or metals
        // symbol with no indicators — and this placer confines every trade card and yearly plaque
        // to the rectangle it computes here. Recomputing that condition would be a second copy of
        // it, and the two disagreeing is precisely the failure the comment on `tradeLabelBounds`
        // describes: a clamp holding a card inside a rectangle the plot no longer occupies, which
        // looks exactly like the clamp being broken. Deriving it from the option that shipped
        // means there is nothing to disagree with.
        const mainHeightPct = parseFloat(String(finalChartOptions?.grid?.[0]?.height ?? ''));
        const placerDivider = Number.isFinite(mainHeightPct) ? mainHeightPct + 2.5 : dividerPercent;
        const grid = gridRect({ width, height, dividerPercent: placerDivider });
        const anchors = {};
        cards.forEach((c) => {
            // The MARKER's coordinate -- the entry or exit triangle. By bar INDEX, because the x
            // axis is a category axis and that is what it takes.
            const px = chart.convertToPixel({ xAxisIndex: 0, yAxisIndex: 0 }, [c.index, c.price]);
            if (!Array.isArray(px) || !Number.isFinite(px[0])) return;
            if (c.anchorTop) {
                // A YEARLY SUMMARY hangs from the top margin. Only its X comes from the data --
                // the year it covers -- so it still tracks the zoom, while its Y is the plot's
                // own edge rather than a price. There is no price for a year, and inventing one
                // would place the plaque at a level nothing computed.
                anchors[c.key] = [px[0], grid.top];
                return;
            }
            if (Number.isFinite(px[1])) anchors[c.key] = px;
        });

        const offsets = {};
        cardOffsetsRef.current.forEach((v, k) => { offsets[k] = v; });
        const placements = layoutCards({ cards, anchors, grid, offsets });
        const graphics = cardGraphics({
            placements,
            cards,
            onMoved: (key, off) => cardOffsetsRef.current.set(key, off),
        });

        // THE WATERMARK TRAVELS WITH THEM. `replaceMerge` is what removes a card that has been
        // closed -- without it a closed card's graphic would stay on the chart forever -- but it
        // replaces the WHOLE graphic component, so anything else living there has to be re-sent in
        // the same call. The watermark is the one thing that does.
        const base = Array.isArray(finalChartOptions?.graphic) ? finalChartOptions.graphic : [];
        chart.setOption({ graphic: [...base, ...graphics] }, { replaceMerge: ['graphic'] });
    }, [dividerPercent, finalChartOptions]);

    /**
     * One pass per animation frame, however many zoom events arrive.
     *
     * <p>A WHEEL GESTURE fires `datazoom` continuously, and every pass re-converts an anchor per
     * open card -- so coalescing to the paint rate is the difference between a smooth zoom and a
     * stuttering one.
     *
     * <p>EXCEPT WHEN THE DOCUMENT IS HIDDEN, where it renders straight away. A hidden tab gets no
     * animation frames at all, so the callback would sit queued and -- because the pending flag
     * stays set -- every later request would be dropped on the floor too. That is not just a
     * background-tab curiosity: `utils/chartSweepCapture.js` drives a programmatic zoom and then
     * calls `getDataURL`, and an export running on a hidden tab would otherwise capture every
     * card at the position it held before the zoom.
     */
    const scheduleTradeCards = useCallback(() => {
        if (typeof document !== 'undefined' && document.hidden) {
            renderTradeCards();
            return;
        }
        if (cardFrameRef.current) return;
        cardFrameRef.current = requestAnimationFrame(() => {
            cardFrameRef.current = 0;
            renderTradeCards();
        });
    }, [renderTradeCards]);

    useEffect(() => {
        scheduleCardsRef.current = scheduleTradeCards;
        // Twice: once now, and once after the first paint. On mount the instance exists but has
        // not been laid out, so its size is 0 and every card would be placed inside a degenerate
        // rectangle -- the same reason the pixel-size effect settles on a timeout.
        renderTradeCards();
        const settle = setTimeout(renderTradeCards, 0);
        window.addEventListener('resize', scheduleTradeCards);
        return () => {
            clearTimeout(settle);
            window.removeEventListener('resize', scheduleTradeCards);
            if (cardFrameRef.current) cancelAnimationFrame(cardFrameRef.current);
            cardFrameRef.current = 0;
        };
    }, [renderTradeCards, scheduleTradeCards, openDecorators]);


    // --- Stable onEvents: must NOT be recreated each render or echarts-for-react
    //     will dispose + recreate the entire ECharts instance (causes flicker). ---
    const stableOnEvents = useMemo(() => ({
        mousemove: (params) => {
            if (!params || !params.name) return;
            const rawDate = params.name;
            // Convert formatted date (e.g. "04/18/2025") back to yyyy-MM-dd
            let dateStr = rawDate;
            const parts = rawDate.split('/');
            if (parts.length === 3 && parts[0].length <= 2) {
                dateStr = `${parts[2]}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`;
            }
            floaterDateRef.current = dateStr;
            // The Special Study is click-driven; a pointer passing over a bar must not move,
            // refetch or replace the pinned card.
            if (floaterModeRef.current !== 'ohlcv' && !isSpecialRef.current) {
                // Use ECharts convertToPixel to pin the floater to the bar's x-coordinate
                let px = 0, py = 10;
                try {
                    const inst = chartRef.current?.getEchartsInstance?.();
                    if (inst && params.dataIndex != null) {
                        const pixel = inst.convertToPixel({ xAxisIndex: 0 }, params.dataIndex);
                        if (pixel != null) px = pixel + 16;
                    }
                } catch (_) { /* fallback */ }
                if (!px) {
                    // Fallback: use the native DOM event offset
                    const evt = params.event?.event || params.event;
                    px = (evt?.offsetX || 0) + 16;
                }
                setFloaterPos({ x: px, y: py, visible: true });
                // Trigger fetch (cached results return instantly)
                if (fetchFloaterRef.current) fetchFloaterRef.current(dateStr);
            }
        },
        globalout: () => {
            // A pinned card survives the pointer leaving the chart - that is what pinned means.
            if (isSpecialRef.current) return;
            setFloaterPos(p => ({ ...p, visible: false }));
            floaterDateRef.current = null;
        },
        click: (params) => {
            // A TRADE LINE. The clicked HALF is read straight off the datum -- the renderer split
            // the connector at its chronological midpoint and gave each half its own datum, so
            // there is no pixel arithmetic here and nothing to redo on zoom.
            const datum = params && params.data;
            if (datum && datum.tradeKey && datum.half) {
                tradeClickRef.current = true;
                // ONE key for a half, TWO for a BOTH segment -- a trade too short to split has
                // no combined card to open, so its single segment reaches both of its cards.
                const keys = cardKeysFor(datum.tradeKey, datum.half);
                setOpenDecorators((prev) => {
                    const next = new Set(prev);
                    // TOGGLE: a second click on the same half puts the card away. The brief asks
                    // for the decorator to be brought up by clicking; it has to go away the same
                    // way, or a reader who opens twenty cards has no way back to a clean chart.
                    //
                    // For a BOTH segment the pair moves TOGETHER, on whether the first is open --
                    // toggling each independently would leave one up and one down whenever they
                    // had been changed separately, so a click would appear to do nothing.
                    const opening = !next.has(keys[0]);
                    keys.forEach((k) => (opening ? next.add(k) : next.delete(k)));
                    return next;
                });
                return;
            }
            /*
             * A SIGNAL MARKER, into the Interactive Trade Simulator.
             *
             * Read off the DATUM, like the trade line above, rather than from the series id: any
             * overlay that stamps `simSignal` on a point becomes clickable, which is the seam
             * PTA and PTB plug into later.
             *
             * It CLAIMS the click through the same flag, so one gesture on a marker does not also
             * select a bar, start a drawing or reveal a replay bar. Without that, clicking a CTS
             * point in simulate mode would both open the card and move the playhead.
             */
            if (datum && datum.simSignal) {
                tradeClickRef.current = true;
                const cb = onSignalClickRef.current;
                if (cb) cb(datum.simSignal);
                return;
            }
            // In Special Study the DOM-level click handler below owns the card: it can tell a bar
            // from the background, which this series-level event cannot. Hiding here would close
            // the card in the same gesture that opened it.
            if (isSpecialRef.current) return;
            setFloaterPos(p => ({ ...p, visible: false }));
            floaterDateRef.current = null;
        },
        datazoom: (params) => {
            // THE CARDS ARE PLACED IN PIXELS, so a zoom moves every anchor out from under them.
            // Coalesced to one pass per frame -- a wheel gesture fires this continuously.
            if (scheduleCardsRef.current) scheduleCardsRef.current();
            // A user's wheel/drag arrives batched; a programmatic dispatchAction arrives with the
            // values at the TOP level and no batch at all. Reading only the batch meant a
            // programmatic zoom moved the view without ever reaching zoomRef — so the next render
            // rebuilt the option from the stale ref and snapped the view straight back.
            //
            // That is what made the Elliott "focus on the count" do nothing: it dispatched, the
            // view moved, addOverlay re-rendered, and the old window won. zoomRef has to record
            // where the chart IS, however it got there.
            const batch = params && params.batch && params.batch.length > 0 ? params.batch[0] : params;
            if (batch) {
                if (typeof batch.startValue !== 'undefined' && typeof batch.endValue !== 'undefined') {
                    zoomRef.current = {
                        startValue: batch.startValue,
                        endValue: batch.endValue,
                    };
                    // Only update the ref — do NOT call setZoomWindow here.
                    // ECharts handles zoom natively; calling setZoomWindow would
                    // trigger memo recomputation → notMerge:true full rebuild on
                    // every scroll, which corrupts the chart.
                }
            }
            // Auto-deactivate area zoom tool so cursor doesn't stay as "+"
            try {
                const inst = chartRef.current?.getEchartsInstance?.();
                if (inst) {
                    inst.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: false });
                }
            } catch (_) { /* ignore */ }
        },
    }), []); // eslint-disable-line react-hooks/exhaustive-deps

    const stableLoadingOption = useMemo(() => ({ text: 'Loading...' }), []);

    // --- We manage setOption ourselves: clear() + setOption() for full updates,
    //     ID-targeted patch for study bar reveals. ---
    const shouldSetOption = useMemo(() => () => false, []);

    // Study reveal: cheap incremental patch (avoids expensive finalChartOptions recomputation)
    useEffect(() => {
        if (!studyModeRef.current) {
            studyBarCountRef.current = 0;
            return;
        }
        const currentBarData = barDataRef.current;
        const barCount = currentBarData?.realBarCount || 0;
        if (studyBarCountRef.current > 0 && barCount > studyBarCountRef.current) {
            // Incremental study reveal — patch only price + volume data.
            //
            // The patch has to restate the series type, and it must be the type the current view
            // asks for. Hardcoding 'candlestick' here meant a study reveal silently converted a
            // line chart into candles mid-run; with the hidden view it would also have undone the
            // hiding, since a candlestick series carries none of the zero-opacity styling.
            const instance = chartRef.current?.getEchartsInstance?.();
            if (instance) {
                // THE PATCH IS SHARED with the replay's bar-by-bar unveiling, which needs exactly
                // this: a merge by id that updates two series and leaves the other thirty alone.
                // One definition, because the type and the hidden view's styling are both things
                // a second copy would get wrong -- and did, before the type became an argument.
                instance.setOption({
                    series: pricePatchSeries(
                        chartTypeRef.current, currentBarData.ohlc, currentBarData.volumes),
                }, false);
            }
        }
        studyBarCountRef.current = barCount;
    }, [propsBarData]);

    // Full chart update (runs when finalChartOptions actually changes — NOT on study reveals)
    useEffect(() => {
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        if (!finalChartOptions || Object.keys(finalChartOptions).length === 0) return;

        // Hide any active tooltip first — notMerge disposes old components,
        // and an in-flight tooltip would crash with "Cannot set innerHTML of null".
        try { instance.dispatchAction({ type: 'hideTip' }); } catch (_) { /* ignore */ }

        // Full update: notMerge replaces all components atomically — equivalent
        // to a fresh chart but without destroying DOM elements prematurely.
        instance.setOption(finalChartOptions, { notMerge: true });
    }, [finalChartOptions]);

    return (
        // THE CANVAS BACKGROUND IS THE CONTAINER'S, not the chart's. ECharts draws on a
        // transparent canvas on both themes, so this one `bgcolor` is the whole of what the
        // reader sees behind the price — and because it is `background.default` it cannot
        // disagree with the workspace around it.
        <Box ref={chartWrapperRef} sx={{ position: 'relative', width: '100%', height: '100%',
            bgcolor: 'background.default' }}>
            <ReactECharts
                ref={chartRef}
                option={finalChartOptions}
                shouldSetOption={shouldSetOption}
                style={{ height: '100%', width: '100%' }}
                onEvents={stableOnEvents}
                loadingOption={stableLoadingOption}
                showLoading={propsLoading || !propsBarData || propsBarData.ohlc.length === 0}
            />
            {/* The Special Study card: pinned, closeable, and the one floater a pointer can reach. */}
            {isSpecialFloater && floaterPos.visible && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: floaterPos.x, top: floaterPos.y, zIndex: 2100,
                        bgcolor: 'rgba(255,255,255,0.98)',
                        border: '1px solid #90a4ae', borderRadius: 1, boxShadow: 3,
                        px: 1.5, py: 1,
                        // The one floater that accepts the pointer, because it is the one carrying a
                        // control. The others are read and left, so they stay click-through.
                        pointerEvents: 'auto',
                        fontSize: 12, fontFamily: 'monospace',
                        maxWidth: 460, maxHeight: 380, overflowY: 'auto', whiteSpace: 'pre-wrap',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ fontWeight: 'bold', fontSize: 13, color: '#1976d2', flexGrow: 1 }}>
                            {`Special Study \u2014 ${floaterData?.date || floaterDateRef.current || ''}`}
                        </Box>
                        <Box
                            component="span"
                            role="button"
                            aria-label="Close the Special Study"
                            onClick={() => {
                                setFloaterPos(p => ({ ...p, visible: false }));
                                floaterDateRef.current = null;
                            }}
                            sx={{
                                cursor: 'pointer', px: 0.75, fontWeight: 700, color: '#607d8b',
                                '&:hover': { color: '#263238' },
                            }}
                        >
                            {'\u00d7'}
                        </Box>
                    </Box>
                    {!floaterData && <Box sx={{ opacity: 0.6 }}>reading the sky...</Box>}
                    {floaterData?.error && <Box sx={{ color: '#c62828' }}>{floaterData.error}</Box>}
                    {Array.isArray(floaterData?.lines) && floaterData.lines.map((line, i) => (
                        <Box
                            key={i}
                            sx={{
                                // A blank line is the separator the study specifies between the two
                                // viewpoints, so it needs a height of its own or it collapses.
                                minHeight: line === '' ? 10 : undefined,
                                fontWeight: /^[A-Z]+$/.test(line) ? 700 : 400,
                                color: /^[A-Z]+$/.test(line) ? '#37474f' : '#222',
                                mt: (/^[A-Z]+$/.test(line) && i > 0) ? 0.5 : 0,
                            }}
                        >
                            {line}
                        </Box>
                    ))}
                    {floaterData?.note && (
                        <Box sx={{ mt: 0.75, fontSize: 11, opacity: 0.7 }}>{floaterData.note}</Box>
                    )}
                </Box>
            )}
            {/* Planetary Floater Overlay */}
            {barFloaterMode !== 'ohlcv' && !isSpecialFloater && floaterPos.visible && floaterData && !floaterData.error && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: floaterPos.x,
                        top: floaterPos.y,
                        zIndex: 2000,
                        bgcolor: 'rgba(255,255,255,0.96)',
                        border: '1px solid #bbb',
                        borderRadius: 1,
                        boxShadow: 2,
                        px: 1.5, py: 1,
                        pointerEvents: 'none',
                        fontSize: 12,
                        fontFamily: 'monospace',
                        maxWidth: 420,
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Box sx={{ fontWeight: 'bold', mb: 0.5, fontSize: 13, color: '#1976d2' }}>
                        {floaterData.date}
                    </Box>
                    {/* SHORT BAR MODE suppresses the astrological floater outright. The preset is
                        the quantitative one - it renames the product and strips two runs of
                        astrological toolbar icons - so leaving a card of planetary degrees on
                        hover would contradict the rest of the preset. */}
                    {!shortBarMode
                        && (barFloaterMode === 'planets_sid' || barFloaterMode === 'planets_trop')
                        && floaterData.planets && (
                        <table style={{ borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid #ccc' }}>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Planet</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Long°</th>
                                    <th style={{ textAlign: 'left', padding: '1px 6px' }}>Sign</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Decl°</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Dist(AU)</th>
                                    <th style={{ textAlign: 'right', padding: '1px 6px' }}>Speed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {floaterData.planets.map((p, i) => (
                                    <tr key={i} style={{ color: p.retro ? '#d32f2f' : '#222' }}>
                                        <td style={{ padding: '1px 6px' }}>{p.name}</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.longitude}°</td>
                                        <td style={{ padding: '1px 6px' }}>{p.sign} {p.degInSign}°</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.declination}°</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.distance}</td>
                                        <td style={{ textAlign: 'right', padding: '1px 6px' }}>{p.speed}{p.retro ? ' R' : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!shortBarMode
                        && (barFloaterMode === 'aspects_geo' || barFloaterMode === 'aspects_helio')
                        && floaterData.aspects && (() => {
                        // Fixed-width columns rather than a table. A table aligns by construction;
                        // text lines align by arithmetic, which is why the padding lives in a tested
                        // module and only the rendering is here.
                        const lines = buildAspectFloaterLines(floaterData.aspects);
                        const counts = filterSummary(floaterData.aspects);
                        return (
                            <Box sx={{
                                // Named explicitly rather than left to the card's `monospace`: the
                                // generic family resolves to whatever the machine happens to have,
                                // and a proportional fallback would silently undo every column.
                                fontFamily: "'Roboto Mono', 'Courier New', monospace",
                                fontSize: 11.5,
                                lineHeight: 1.5,
                                whiteSpace: 'pre',
                            }}>
                                {lines.length === 0 && (
                                    <Box sx={{ color: '#888' }}>No aspects within orb</Box>
                                )}
                                {lines.map((l, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            // A blank separator needs a height of its own or it
                                            // collapses and the groups run together.
                                            minHeight: l.blank ? 9 : undefined,
                                            color: l.color || 'inherit',
                                            fontWeight: l.aspect && l.aspect.aspect === 'Trine' ? 700 : 400,
                                        }}
                                    >
                                        {l.text}
                                    </Box>
                                ))}
                                {counts.dropped > 0 && (
                                    // Said out loud. A shorter list than the reader saw yesterday
                                    // otherwise reads as a quieter sky rather than a tighter filter.
                                    <Box sx={{ mt: 0.5, fontSize: 10, opacity: 0.65 }}>
                                        {`${counts.dropped} outside orb (${ORB_LIMIT_WITH_MOON.toFixed(2)}° with the Moon, ${ORB_LIMIT_DEFAULT.toFixed(2)}° otherwise)`}
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </Box>
            )}
            {/* Text Annotation Overlays */}
            <TextAnnotationLayer
                overlays={overlays}
                chartRef={chartRef}
                onUpdateOverlay={onUpdateOverlay}
            />
        </Box>
    );
});

// --- Text Annotation Layer component ---
function TextAnnotationLayer({ overlays, chartRef, onUpdateOverlay }) {
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    const textOverlays = useMemo(
        () => (overlays || []).filter(o => o.type === 'textAnnotation'),
        [overlays]
    );

    // Compute pixel positions from data coordinates
    const [positions, setPositions] = useState({});
    const updatePositions = useCallback(() => {
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        const newPositions = {};
        textOverlays.forEach(overlay => {
            // If the overlay has a pixel offset from dragging, use that
            if (overlay.pixelX != null && overlay.pixelY != null) {
                newPositions[overlay.id] = { x: overlay.pixelX, y: overlay.pixelY };
                return;
            }
            // Otherwise compute from date + price
            try {
                const dates = instance.getOption()?.xAxis?.[0]?.data;
                if (!dates) return;
                const dateStr = overlay.date?.split?.('T')?.[0] || overlay.date;
                const dataIndex = dates.indexOf(dateStr);
                if (dataIndex === -1) return;
                const pixel = instance.convertToPixel({ gridIndex: 0 }, [dataIndex, overlay.price]);
                if (pixel) {
                    newPositions[overlay.id] = { x: pixel[0], y: pixel[1] };
                }
            } catch (_) { /* ignore */ }
        });
        setPositions(newPositions);
    }, [textOverlays, chartRef]);

    // Update positions on render and on zoom/resize
    useEffect(() => {
        updatePositions();
        const instance = chartRef.current?.getEchartsInstance?.();
        if (!instance) return;
        const handler = () => updatePositions();
        instance.on('dataZoom', handler);
        instance.on('restore', handler);
        window.addEventListener('resize', handler);
        return () => {
            try { instance.off('dataZoom', handler); } catch (_) { /* cleanup */ }
            try { instance.off('restore', handler); } catch (_) { /* cleanup */ }
            window.removeEventListener('resize', handler);
        };
    }, [updatePositions, chartRef]);

    // Also update whenever finalChartOptions changes (detected via overlays length change)
    useEffect(() => {
        const timer = setTimeout(updatePositions, 100);
        return () => clearTimeout(timer);
    }, [overlays, updatePositions]);

    const handleDragStart = useCallback((e, overlayId) => {
        e.preventDefault();
        e.stopPropagation();
        const startX = e.clientX;
        const startY = e.clientY;
        const startPos = positions[overlayId] || { x: 0, y: 0 };

        const onMove = (me) => {
            const dx = me.clientX - startX;
            const dy = me.clientY - startY;
            setPositions(prev => ({
                ...prev,
                [overlayId]: { x: startPos.x + dx, y: startPos.y + dy }
            }));
        };
        const onUp = (me) => {
            const dx = me.clientX - startX;
            const dy = me.clientY - startY;
            const finalX = startPos.x + dx;
            const finalY = startPos.y + dy;
            if (onUpdateOverlay) {
                onUpdateOverlay(overlayId, { pixelX: finalX, pixelY: finalY });
            }
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [positions, onUpdateOverlay]);

    const handleDoubleClick = useCallback((e, overlay) => {
        e.preventDefault();
        e.stopPropagation();
        setEditingId(overlay.id);
        setEditText(overlay.text || '');
    }, []);

    const commitEdit = useCallback(() => {
        if (editingId && onUpdateOverlay) {
            onUpdateOverlay(editingId, { text: editText });
        }
        setEditingId(null);
    }, [editingId, editText, onUpdateOverlay]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            commitEdit();
        } else if (e.key === 'Escape') {
            setEditingId(null);
        }
    }, [commitEdit]);

    if (textOverlays.length === 0) return null;

    return (
        <>
            {textOverlays.map(overlay => {
                const pos = positions[overlay.id];
                if (!pos) return null;
                const isEditing = editingId === overlay.id;
                // Each note renders at the size it was placed with. Notes created before this
                // existed carry no fontSize, and BASE_TEXT_SIZE is exactly what they were drawn
                // at, so they are unchanged.
                const fontSize = overlay.fontSize || BASE_TEXT_SIZE;

                return (
                    <div
                        key={overlay.id}
                        style={{
                            position: 'absolute',
                            left: pos.x,
                            top: pos.y,
                            transform: 'translate(-50%, -100%)',
                            zIndex: 1500,
                            cursor: isEditing ? 'text' : 'grab',
                            userSelect: 'none',
                            pointerEvents: 'auto',
                        }}
                        onMouseDown={!isEditing ? (e) => handleDragStart(e, overlay.id) : undefined}
                        onDoubleClick={(e) => handleDoubleClick(e, overlay)}
                    >
                        {isEditing ? (
                            <input
                                autoFocus
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onBlur={commitEdit}
                                onKeyDown={handleKeyDown}
                                style={{
                                    fontSize,
                                    fontWeight: 'bold',
                                    color: overlay.color || '#7b1fa2',
                                    border: '1px solid #7b1fa2',
                                    borderRadius: 3,
                                    padding: '2px 6px',
                                    outline: 'none',
                                    background: 'rgba(255,255,255,0.95)',
                                    minWidth: 40,
                                }}
                            />
                        ) : (
                            <span
                                style={{
                                    // Editing and display must match, or text jumps size the
                                    // moment you double-click it.
                                    fontSize,
                                    fontWeight: 'bold',
                                    color: overlay.color || '#7b1fa2',
                                    background: 'rgba(255,255,255,0.85)',
                                    borderRadius: 3,
                                    padding: '2px 6px',
                                    border: '1px solid transparent',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {overlay.text || 'Note'}
                            </span>
                        )}
                    </div>
                );
            })}
        </>
    );
}

TextAnnotationLayer.propTypes = {
    overlays: PropTypes.array,
    chartRef: PropTypes.object,
    onUpdateOverlay: PropTypes.func,
};

EChartComponent.propTypes = {
    /** Called with the number of open trade cards whenever that changes. */
    onTradeCardsChanged: PropTypes.func,
    /** The replay reveal index, or null. Read by the final gate; see finalChartOptions. */
    simulationBoundary: PropTypes.number,
    /** The last column with a price, as a date. See the destructured default above. */
    revealedUpToDate: PropTypes.string,
    lastTradedDate: PropTypes.string,
    barData: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    style: PropTypes.object,
    chartType: PropTypes.string,
    viewMode: PropTypes.string,
    chartColors: PropTypes.object,
    overlays: PropTypes.array,
    maConfigs: PropTypes.array,
    showRSI: PropTypes.bool,
    rsiData: PropTypes.array,
    showMACD: PropTypes.bool,
    macdData: PropTypes.object,
    setPatternDialogOpen: PropTypes.func.isRequired,
    onBarSelect: PropTypes.func,
    /** Called with a datum's `simSignal` when a signal marker is clicked. */
    onSignalClick: PropTypes.func,
    /**
     * Right-click or shift-click on a bar. Receives {date, dataIndex, price, clientX, clientY} -
     * the screen coordinates included so the caller can position a menu without re-reading the
     * event, which it no longer has.
     */
    onBarContextMenu: PropTypes.func,
    studyMode: PropTypes.bool,
    barFloaterMode: PropTypes.string,
    /** The toolbar preset. True suppresses the astrological floater outright. */
    shortBarMode: PropTypes.bool,
    /** An administrator is an exception to the rules made for other readers. Hover cards only. */
    isAdmin: PropTypes.bool,
    /** The customer's configured chart colours, by role. See utils/workspaceColors. */
    workspaceColors: PropTypes.objectOf(PropTypes.string),
    symbol: PropTypes.string,
    watermarkText: PropTypes.string,
    watermarkOverride: PropTypes.shape({
        hidden: PropTypes.bool,
        text: PropTypes.string,
        fontSize: PropTypes.number,
    }),
    onUpdateOverlay: PropTypes.func,
};

EChartComponent.displayName = 'EChartComponent';

export default EChartComponent;